import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  FiRefreshCw, FiSearch, FiX, FiAlertTriangle,
  FiChevronLeft, FiChevronRight, FiChevronDown, FiChevronUp, FiCheck, FiExternalLink,
  FiUser, FiFileText, FiPaperclip, FiSend, FiMail, FiEye, FiLink2, FiPlus, FiEdit2, FiFilter,
  FiInfo, FiCheckSquare,
} from 'react-icons/fi';
import { listarProcessos } from '../../services/processos';
import {
  listarObrigacoesNibo, listarClientesDaObrigacao, listarUsuariosNiboAPI, listarDepartamentosNiboAPI,
  vincularClientesObrigacaoNibo, desvincularClienteObrigacaoNibo,
  listarProtocolosNibo, buscarCalendarioNibo, listarClientesNibo,
  buscarDetalheProtocoloNibo, sincronizarNibo,
} from '../../services/rotinas';
import { listarClientes as listarNossosClientes, atualizarCliente as atualizarNossoCliente } from '../../services/clientes';
import { listarTarefas, atualizarTarefa } from '../../services/tarefas';
import {
  listarPerguntasFechamento, criarPerguntaFechamento, atualizarPerguntaFechamento,
} from '../../services/perguntasFechamento';
import {
  listarEtapasObrigacao,
  listarConclusoesEtapas, listarConclusoesEtapasPorCompetencia, alternarConclusaoSubitem,
} from '../../services/etapasObrigacao';
import styles from './Rotinas.module.css';
import GruposFluxo, { AbaExecucao as ExecucaoGrupoFluxo } from './GruposFluxo';
import VisaoVencimentos from './VisaoVencimentos';
import TarefasPagina from './Tarefas';
import CadastroRotinas from './CadastroRotinas';
import { listarGrupos } from '../../services/gruposFluxo';
import Processos from '../processos';
import useEsc from '../../hooks/useEsc';

/* Painel: abas e botão ocultos (não apagados) -- ver o comentário na
   lista de abas. Visíveis: Vencimentos e Processos. */
const ABAS_OCULTAS = ['calendario', 'auditoria', 'tarefas', 'protocolos', 'obrigacoes', 'etapas'];
const OCULTAR_SINCRONIZAR_NIBO = true;

/* abas que eram itens do menu: respeitam a permissão que o item tinha */
const PERMISSAO_ABA = { tarefas_lista: 'rotinas/tarefas', cadastro: 'rotinas/cadastro', processos: 'rotinas/processos' };
function podeVerAba(key) {
  if (!PERMISSAO_ABA[key]) return true;
  try {
    const perm = JSON.parse(localStorage.getItem('erp_perm'));
    if (!perm || perm.admin) return true;
    return (perm.menus || []).includes(PERMISSAO_ABA[key]) || (perm.menus || []).includes('rotinas');
  } catch { return true; }
}

/* modais só fecham pelo botão de X ou pela tecla Esc — clicar fora nunca fecha
   (evita perder edição em andamento por um clique sem querer no fundo) */
function useFecharComEsc(onClose) {
  useEffect(() => {
    function aoTeclar(e) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', aoTeclar);
    return () => document.removeEventListener('keydown', aoTeclar);
  }, [onClose]);
}

const LIMITE = 15;
const DIAS_SEMANA     = ['DOM', 'SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SAB'];
const DIAS_SEMANA_EXT = ['DOM', 'SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SAB'];
const MESES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

/* status do protocolo (reports/obligations/complete): 1 Excluído, 2 Cancelado,
   3 Não Recebido, 4 Recebido, 5 Não ativo, 6 Ativo, 7 Baixa Justificada, 8 Pago */
const STATUS_PROTOCOLO = {
  1: { label: 'Excluído',          css: 'statusNeutro' },
  2: { label: 'Cancelado',         css: 'statusNeutro' },
  3: { label: 'Aguardando',        css: 'statusPendente' },
  4: { label: 'Recebido',          css: 'statusOk' },
  5: { label: 'Não ativo',         css: 'statusNeutro' },
  6: { label: 'Ativo',             css: 'statusPendente' },
  7: { label: 'Baixa justificada', css: 'statusNeutro' },
  8: { label: 'Pago',              css: 'statusOk' },
};

/* tipo/frequência da obrigação (documentado em "Dicionário para propriedades
   retornadas" da própria API) */
const TIPO_OBRIGACAO = { 1: 'Pagamento', 2: 'Cadastral', 3: 'Declaração', 4: 'Diversos' };
const FREQUENCIA_OBRIGACAO = {
  0: 'Diário', 1: 'Decendial', 2: 'Quinzenal', 3: 'Mensal', 4: 'Bimestral',
  5: 'Trimestral', 6: 'Quadrimestral', 7: 'Semestral', 8: 'Anual',
};

function formatarData(d) {
  if (!d) return '—';
  /* data "pura" (sem hora) -- new Date('2026-07-16') é interpretado como
     UTC e o toLocaleDateString em fuso negativo (Brasil) volta um dia
     (mostrava 15/07 pra uma tarefa vencendo 16/07). Monta a partir dos
     componentes, sem passar pelo Date, pra não sofrer esse deslocamento. */
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(d);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return d;
  return dt.toLocaleDateString('pt-BR');
}

function formatarDataHora(d) {
  if (!d) return '—';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return d;
  return dt.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function formatarMoeda(v) {
  if (v == null) return '—';
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

/* clientes vêm da Nibo como "NNN - Razão social" -- usa o código numérico
   pra ordenar a visão em Lista, igual pedido ("clientes por ordem do código") */
function codigoCliente(nomeCliente) {
  const m = (nomeCliente || '').match(/^(\d+)/);
  return m ? m[1] : (nomeCliente || '');
}

/* setores usados no fechamento por cliente (aba Auditoria) -- mapeados a
   partir do departamento que já vem em cada obrigação/tarefa da Nibo.
   Tarefa interna não tem departamento (fica de fora das 3, só aparece em
   "Todos"); se o nome do departamento configurado na Nibo desta conta for
   diferente disso, ajustar esse mapeamento. */
const SETORES = [
  { key: 'fiscal',   label: 'Fiscal' },
  { key: 'folha',    label: 'Folha' },
  { key: 'contabil', label: 'Contábil' },
];
function setorDeItem(it) {
  const d = (it.departamento || '').toLowerCase();
  if (d.includes('fiscal')) return 'fiscal';
  if (d.includes('folha') || d.includes('pessoal')) return 'folha';
  if (d.includes('cont')) return 'contabil';
  return null;
}

function formatarCompetencia(d) {
  if (!d) return '—';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return d;
  return dt.toLocaleDateString('pt-BR', { month: '2-digit', year: 'numeric' });
}

/* Y-M-D local, sem passar por UTC (evita o dia "pular" por causa do fuso) */
function paraISO(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function primeiroDiaMes(d = new Date()) { return paraISO(new Date(d.getFullYear(), d.getMonth(), 1)); }
function ultimoDiaMes(d = new Date())    { return paraISO(new Date(d.getFullYear(), d.getMonth() + 1, 0)); }

function formatarDiaSemana(diaStr) {
  const [y, m, d] = diaStr.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  return `${DIAS_SEMANA_EXT[dt.getDay()]}, ${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}/${y}`;
}

function construirGrade(mesRef) {
  const primeiro = new Date(mesRef.getFullYear(), mesRef.getMonth(), 1);
  const inicioGrade = new Date(primeiro);
  inicioGrade.setDate(inicioGrade.getDate() - primeiro.getDay());
  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(inicioGrade);
    d.setDate(d.getDate() + i);
    return d;
  });
}

/* ── aviso de dados mockados (Nibo indisponível) -- ícone fixo no canto
   superior direito da tela (não empurra o conteúdo da aba), clique expande
   a mensagem. Usado em várias abas; posição fixa evita depender de onde na
   árvore cada uma acaba renderizando ele. ── */
function AvisoMock() {
  const [aberto, setAberto] = useState(false);
  return (
    <div className={styles.avisoMockWrap}>
      <button
        type="button"
        className={styles.avisoMockIcone}
        onClick={() => setAberto(v => !v)}
        title="Não foi possível conectar à API do Nibo — exibindo dados de exemplo"
      >
        <FiAlertTriangle size={13} />
      </button>
      {aberto && (
        <div className={styles.avisoMockBalao}>
          <span>Não foi possível conectar à API do Nibo — exibindo dados de exemplo.</span>
          <button type="button" className={styles.avisoFechar} onClick={() => setAberto(false)}><FiX size={13} /></button>
        </div>
      )}
    </div>
  );
}

/* ── hook de lista paginada + busca, reaproveitado pelas abas em tabela ──
   aceita filtros extras (ex.: período de vencimento) além da busca por texto */
function usePaginado(fetchFn, filtrosIniciais = {}) {
  const carregouRef = useRef(false);
  const [dados, setDados]             = useState({ items: [], metadata: {} });
  const [pagina, setPagina]           = useState(1);
  const [busca, setBusca]             = useState('');
  const [buscaAtiva, setBuscaAtiva]   = useState('');
  const [filtros, setFiltros]         = useState(filtrosIniciais);
  const [carregando, setCarregando]   = useState(false);
  const [erro, setErro]               = useState('');

  const carregar = useCallback(async (pg = 1, s = buscaAtiva, f = filtros) => {
    carregouRef.current = true;
    setCarregando(true); setErro('');
    try {
      const res = await fetchFn({ search: s, top: LIMITE, skip: (pg - 1) * LIMITE, ...f });
      setDados(res || { items: [], metadata: {} });
      setPagina(pg);
    } catch (e) {
      setErro(e.message);
    } finally {
      setCarregando(false);
    }
  }, [fetchFn, buscaAtiva, filtros]);

  const garantirCarregado = useCallback(() => {
    if (!carregouRef.current) carregar(1, '', filtros);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carregar]);

  function filtrar(e) {
    e.preventDefault();
    setBuscaAtiva(busca);
    carregar(1, busca, filtros);
  }

  function aplicarFiltros(novosFiltros) {
    setFiltros(novosFiltros);
    carregar(1, buscaAtiva, novosFiltros);
  }

  const total = dados.metadata?.total ?? dados.items.length;
  const totalPaginas = Math.max(1, Math.ceil(total / LIMITE));

  return { dados, pagina, busca, setBusca, filtros, aplicarFiltros, carregando, erro, carregar, garantirCarregado, filtrar, total, totalPaginas };
}

/* ── Filtro de período de vencimento (usado na aba Protocolos) — fica na
   mesma linha da barra de busca, por isso não é um <form> próprio ── */
function FiltroPeriodo({ filtros, aplicarFiltros }) {
  const [dataInicio, setDataInicio] = useState(filtros.dataInicio || '');
  const [dataFim, setDataFim]       = useState(filtros.dataFim || '');

  return (
    <div className={styles.filtroPeriodo}>
      <span className={styles.filtroPeriodoLabel}>Vencimento de</span>
      <input type="date" className={styles.filtroPeriodoInput} value={dataInicio} onChange={e => setDataInicio(e.target.value)} />
      <span className={styles.filtroPeriodoLabel}>até</span>
      <input type="date" className={styles.filtroPeriodoInput} value={dataFim} onChange={e => setDataFim(e.target.value)} />
      <button type="button" className={styles.btnBuscar} onClick={() => aplicarFiltros({ dataInicio, dataFim })}>
        Filtrar
      </button>
    </div>
  );
}

/* ── Barra de busca + paginação, comum às abas em tabela ──
   filtroExtra (ex.: período) fica antes da busca; acoesExtra, depois ── */
function BarraLista({ aba, filtroExtra, acoesExtra }) {
  return (
    <form className={styles.barraFiltro} onSubmit={aba.filtrar}>
      {filtroExtra}
      <div className={styles.buscaWrap}>
        <FiSearch size={14} className={styles.buscaIcone} />
        <input
          className={styles.buscaInput}
          placeholder="Buscar..."
          value={aba.busca}
          onChange={e => aba.setBusca(e.target.value)}
        />
      </div>
      <button type="submit" className={styles.btnBuscar}>Buscar</button>
      <button
        type="button"
        className={styles.btnRefresh}
        onClick={() => aba.carregar(aba.pagina)}
        disabled={aba.carregando}
      >
        <FiRefreshCw size={13} className={aba.carregando ? styles.girando : ''} /> Atualizar
      </button>
      {acoesExtra}
    </form>
  );
}

function Paginacao({ aba }) {
  if (aba.totalPaginas <= 1) return null;
  return (
    <div className={styles.paginacao}>
      <button className={styles.btnPagina} disabled={aba.pagina <= 1 || aba.carregando}
        onClick={() => aba.carregar(aba.pagina - 1)}>
        <FiChevronLeft size={14} /> Anterior
      </button>
      <span className={styles.paginaInfo}>Página {aba.pagina} de {aba.totalPaginas}</span>
      <button className={styles.btnPagina} disabled={aba.pagina >= aba.totalPaginas || aba.carregando}
        onClick={() => aba.carregar(aba.pagina + 1)}>
        Próxima <FiChevronRight size={14} />
      </button>
    </div>
  );
}

function EstadoTabela({ aba, colSpan }) {
  if (aba.carregando) return <tr><td colSpan={colSpan} className={styles.vazio}>Carregando...</td></tr>;
  if (aba.erro)       return <tr><td colSpan={colSpan} className={styles.vazio}>{aba.erro}</td></tr>;
  if (!aba.dados.items.length) return <tr><td colSpan={colSpan} className={styles.vazio}>Nenhum registro encontrado.</td></tr>;
  return null;
}

/* ── Calendário de obrigações ──
   Fonte: protocolos (reports/obligations/complete) agregados por dueDate.
   Cor do dia/badge: vermelho = já passou do prazo, amarelo = vence hoje,
   cinza = ainda dentro do prazo. "Exibir concluídos" inclui as já concluídas
   na contagem (sem distinguir se foram concluídas dentro/fora do prazo —
   simplificação da 1ª versão). */
const FILTROS_CALENDARIO_VAZIOS = {
  clienteId: null, clienteNome: '', departamentoId: '', responsavelNome: '',
  obrigacaoId: '', obrigacaoNome: '', baseData: 'interno',
};

/* junta os prazos da Nibo com as tarefas internas cadastradas em
   Obrigações > Tarefas, no mesmo formato de item que o calendário já usa
   -- assim entram na mesma grade/agrupamento sem duplicar lógica.
   obrigacaoId fica null de propósito: não tem roteiro Nibo pra elas. */
function tarefasParaItensCalendario(tarefas, customerId) {
  return (tarefas.items || [])
    .filter(t => !customerId || t.cliente_nibo_id === customerId)
    .map(t => ({
      id: `tarefa-${t.id}`,
      dueDate: (t.data_vencimento || '').slice(0, 10),
      concluido: !!t.concluido,
      obrigacao: t.titulo,
      obrigacaoId: null,
      cliente: t.cliente_nome || '—',
      clienteNiboId: t.cliente_nibo_id || null,
      clienteDocumento: null,
      departamento: null,
      departamentoId: null,
      responsavel: t.responsavel || null,
      /* competencia da tarefa é só "YYYY-MM" -- vira dia 15 (nunca cai na
         virada do mês) só pra reaproveitar o formatarCompetencia existente
         sem sofrer o mesmo desvio de fuso que já corrigimos no vencimento */
      competencia: t.competencia ? `${t.competencia}-15` : null,
      vencimentoOficial: (t.data_vencimento || '').slice(0, 10),
      origemInterna: true,
      tarefaId: t.id,
      checklist: t.checklist || [],
      checklistObrigatorio: !!t.checklist_obrigatorio,
      observacoes: t.observacoes || null,
      parcelaInfo: t.parcelas_total ? `${t.parcela_numero}/${t.parcelas_total}` : null,
    }));
}

async function buscarCalendarioComTarefas(params) {
  const [calendario, tarefas] = await Promise.all([
    buscarCalendarioNibo(params),
    listarTarefas({ inicio: params.inicio, fim: params.fim }).catch(() => ({ items: [] })),
  ]);
  const itensTarefas = tarefasParaItensCalendario(tarefas, params.customerId);
  return { ...calendario, items: [...(calendario.items || []), ...itensTarefas] };
}

/* só as tarefas internas, sem os prazos da Nibo -- alimenta a aba Tarefas
   (ao lado de Auditoria), que reaproveita o mesmo card "Metas internas"
   pra mostrar as tarefas agrupadas por dia/atividade/cliente. */
async function buscarTarefasComoMetas(params) {
  const tarefas = await listarTarefas({ inicio: params.inicio, fim: params.fim }).catch(() => ({ items: [] }));
  return { items: tarefasParaItensCalendario(tarefas, params.customerId) };
}

/* obrigação concluída na Nibo mas com etapa vinculada ainda incompleta pro
   cliente daquele mês -- mantém o `concluido` fiel ao status real da Nibo
   (não mexe nele: quem olhar o status da obrigação em si vê a verdade), só
   marca `etapasPendentes: true`. É esse flag (não o concluido) que mantém o
   item na lista visível de pendentes com a etiqueta "Etapas pendentes",
   até a etapa também ser marcada -- sinaliza que alguém pulou a etapa. */
async function aplicarGateEtapas(items, etapasPorObrigacao) {
  if (!etapasPorObrigacao || etapasPorObrigacao.size === 0) return items;

  const competencias = [...new Set(
    items
      .filter(it => it.concluido && it.obrigacaoId && etapasPorObrigacao.has(it.obrigacaoId))
      .map(it => (it.dueDate || '').slice(0, 7))
  )];
  if (competencias.length === 0) return items;

  const resultados = await Promise.all(
    competencias.map(c => listarConclusoesEtapasPorCompetencia(c).then(r => r.items || []).catch(() => []))
  );
  const concluidosSet = new Set();
  resultados.forEach(lista => lista.forEach(c => {
    concluidosSet.add(`${c.cliente_nibo_id}::${c.etapa_padrao_id}::${c.subitem_id}`);
  }));

  return items.map(it => {
    if (!it.concluido || !it.obrigacaoId) return it;
    const etapa = etapasPorObrigacao.get(it.obrigacaoId);
    if (!etapa) return it;
    /* item conta como sua própria folha SEMPRE, mesmo com subitens -- o
       item também é uma confirmação (ex.: "Fechamento"), além de cada
       subitem (ex.: "Transmissão DCTFWeb") ser outra, independente. */
    const folhas = (etapa.itens || []).flatMap(i => [i.id, ...(i.subitens || []).map(s => s.id)]);
    if (folhas.length === 0) return it;
    const completa = folhas.every(id => concluidosSet.has(`${it.clienteNiboId}::${etapa.id}::${id}`));
    if (completa) return it;
    return { ...it, etapasPendentes: true };
  });
}

async function buscarCalendarioComEtapas(params, etapasPorObrigacao) {
  const resultado = await buscarCalendarioComTarefas(params);
  const items = await aplicarGateEtapas(resultado.items || [], etapasPorObrigacao);
  return { ...resultado, items };
}

function CalendarioTab({ syncTick }) {
  const [mesRef, setMesRef]                 = useState(() => new Date());
  const [dados, setDados]                   = useState({ items: [] });
  const [carregando, setCarregando]         = useState(true);
  const [erro, setErro]                     = useState('');
  const [agrupamento, setAgrupamento]       = useState('atividade');
  const [mostrarConcluidos, setMostrarConcluidos] = useState(false);
  const [diaSelecionado, setDiaSelecionado] = useState(null);
  const [grupoDetalhe, setGrupoDetalhe]     = useState(null);
  const [filtrosAbertos, setFiltrosAbertos] = useState(false);
  const [filtrosForm, setFiltrosForm]       = useState(FILTROS_CALENDARIO_VAZIOS);
  const [filtrosAplicados, setFiltrosAplicados] = useState(FILTROS_CALENDARIO_VAZIOS);
  const [buscaCliente, setBuscaCliente]     = useState('');
  const [clientesEncontrados, setClientesEncontrados] = useState([]);
  const [buscandoCliente, setBuscandoCliente] = useState(false);
  const [departamentos, setDepartamentos]   = useState([]);
  const [usuarios, setUsuarios]             = useState([]);
  const [obrigacoesFiltro, setObrigacoesFiltro] = useState([]);
  const [clientesDaObrigacao, setClientesDaObrigacao] = useState([]);
  const [carregandoClientesObrigacao, setCarregandoClientesObrigacao] = useState(false);
  const [copiado, setCopiado] = useState(false);
  const [etapasPadrao, setEtapasPadrao] = useState([]);
  const gruposRef = useRef({});

  useEffect(() => {
    listarEtapasObrigacao().then(r => setEtapasPadrao(r.items || [])).catch(() => {});
  }, []);
  /* useMemo com identidade estável (só muda quando etapasPadrao muda de
     verdade) -- assim dá pra listar no dep array dos efeitos abaixo sem
     recriar o Map (e disparar o efeito de novo) a cada render */
  const etapasPorObrigacao = useMemo(() => new Map(
    etapasPadrao.filter(e => e.ativo && e.obrigacao_id).map(e => [e.obrigacao_id, e])
  ), [etapasPadrao]);

  const grade = construirGrade(mesRef);
  const inicio  = paraISO(grade[0]);
  const fim     = paraISO(grade[grade.length - 1]);
  const hojeStr = paraISO(new Date());

  function recarregarCalendario() {
    return buscarCalendarioComEtapas({
      inicio, fim,
      customerId: filtrosAplicados.clienteId || undefined,
      departmentId: filtrosAplicados.departamentoId || undefined,
      baseData: filtrosAplicados.baseData,
    }, etapasPorObrigacao).then(setDados).catch(e => setErro(e.message));
  }

  /* concluir uma tarefa interna direto da lista do calendário -- Nibo não
     tem essa ação por aqui (protocolo se conclui protocolando de verdade),
     só as tarefas cadastradas em Obrigações > Tarefas. */
  async function concluirTarefaCalendario(tarefaId) {
    try {
      await atualizarTarefa(tarefaId, { concluido: true });
      setGrupoDetalhe(null);
      recarregarCalendario();
    } catch (e) {
      setErro(e.message);
    }
  }

  /* reabre uma tarefa interna marcada como concluída por engano -- mesma
     ação, invertida (Nibo continua sem isso: protocolo só volta a pendente
     desfazendo a protocolação de verdade lá). */
  async function reabrirTarefaCalendario(tarefaId) {
    try {
      await atualizarTarefa(tarefaId, { concluido: false });
      setGrupoDetalhe(null);
      recarregarCalendario();
    } catch (e) {
      setErro(e.message);
    }
  }

  /* marcar/desmarcar item do checklist de uma tarefa interna, direto no
     detalhe do dia -- atualiza o grupo aberto e a grade por trás (mesmo
     padrão otimista da aba Tarefas: só recarrega de verdade se der erro). */
  async function alternarChecklistItemCalendario(tarefaId, indice) {
    const item = grupoDetalhe?.itens.find(it => it.tarefaId === tarefaId);
    if (!item) return;
    const novoChecklist = (item.checklist || []).map((ci, i) => i === indice ? { ...ci, concluido: !ci.concluido } : ci);
    setGrupoDetalhe(g => g && { ...g, itens: g.itens.map(it => it.tarefaId === tarefaId ? { ...it, checklist: novoChecklist } : it) });
    setDados(d => ({ ...d, items: (d.items || []).map(it => it.tarefaId === tarefaId ? { ...it, checklist: novoChecklist } : it) }));
    try {
      await atualizarTarefa(tarefaId, { checklist: novoChecklist });
    } catch (e) {
      setErro(e.message);
      recarregarCalendario();
    }
  }

  useEffect(() => {
    setCarregando(true); setErro(''); setDiaSelecionado(null);
    buscarCalendarioComEtapas({
      inicio, fim,
      customerId: filtrosAplicados.clienteId || undefined,
      departmentId: filtrosAplicados.departamentoId || undefined,
      baseData: filtrosAplicados.baseData,
    }, etapasPorObrigacao)
      .then(setDados)
      .catch(e => setErro(e.message))
      .finally(() => setCarregando(false));
  }, [inicio, fim, syncTick, filtrosAplicados, etapasPorObrigacao]);

  /* atualiza sozinho quando o usuário volta pra aba (ex.: depois de disparar no Nibo) */
  useEffect(() => {
    function aoVoltarFoco() {
      buscarCalendarioComEtapas({
        inicio, fim,
        customerId: filtrosAplicados.clienteId || undefined,
        departmentId: filtrosAplicados.departamentoId || undefined,
        baseData: filtrosAplicados.baseData,
      }, etapasPorObrigacao).then(setDados).catch(e => setErro(e.message));
    }
    window.addEventListener('focus', aoVoltarFoco);
    return () => window.removeEventListener('focus', aoVoltarFoco);
  }, [inicio, fim, filtrosAplicados, etapasPorObrigacao]);

  /* departamentos, responsáveis e obrigações pra popular os selects — carrega uma vez só */
  useEffect(() => {
    listarDepartamentosNiboAPI().then(r => setDepartamentos(r.items || [])).catch(() => {});
    listarUsuariosNiboAPI({ apenasAtivos: true }).then(r => setUsuarios(r.items || [])).catch(() => {});
    listarObrigacoesNibo({ top: 200 }).then(r => setObrigacoesFiltro(r.items || [])).catch(() => {});
  }, []);

  /* com obrigação filtrada, a visão Lista precisa de TODOS os clientes
     vinculados a ela (não só quem tem item vencendo no mês visível) --
     busca à parte, independente do agrupamento/mês corrente */
  useEffect(() => {
    if (!filtrosAplicados.obrigacaoId) { setClientesDaObrigacao([]); return; }
    setCarregandoClientesObrigacao(true);
    listarClientesDaObrigacao(filtrosAplicados.obrigacaoId, { top: 200 })
      .then(r => setClientesDaObrigacao(r.items || []))
      .catch(() => setClientesDaObrigacao([]))
      .finally(() => setCarregandoClientesObrigacao(false));
  }, [filtrosAplicados.obrigacaoId]);

  /* busca de cliente por nome/documento, ao digitar (a API não expõe filtro
     por responsável nesse relatório, então esse aqui é filtrado no frontend) */
  useEffect(() => {
    if (!buscaCliente.trim()) { setClientesEncontrados([]); return; }
    setBuscandoCliente(true);
    const timer = setTimeout(() => {
      listarClientesNibo({ search: buscaCliente, top: 8 })
        .then(r => setClientesEncontrados(r.items || []))
        .catch(() => setClientesEncontrados([]))
        .finally(() => setBuscandoCliente(false));
    }, 350);
    return () => clearTimeout(timer);
  }, [buscaCliente]);

  function aplicarFiltros() {
    setFiltrosAplicados(filtrosForm);
    setFiltrosAbertos(false);
  }

  function limparFiltros() {
    setFiltrosForm(FILTROS_CALENDARIO_VAZIOS);
    setFiltrosAplicados(FILTROS_CALENDARIO_VAZIOS);
    setBuscaCliente('');
    setClientesEncontrados([]);
    setFiltrosAbertos(false);
  }

  const filtrosAtivos = !!(filtrosAplicados.clienteId || filtrosAplicados.departamentoId || filtrosAplicados.responsavelNome || filtrosAplicados.obrigacaoId || filtrosAplicados.baseData !== 'interno');

  /* responsável e obrigação filtram no que já veio -- não tem esses filtros
     na API pra esse relatório (tentar "Obligation/Id eq X" derruba a
     chamada inteira na Nibo, por isso filtra aqui) */
  const itensFiltrados = (dados.items || [])
    .filter(i => !filtrosAplicados.responsavelNome || i.responsavel === filtrosAplicados.responsavelNome)
    .filter(i => !filtrosAplicados.obrigacaoId || i.obrigacaoId === filtrosAplicados.obrigacaoId);

  /* a busca traz o grid inteiro (com dias do mês anterior/seguinte pra
     preencher as semanas), mas a lista de Metas internas só deve mostrar o
     mês selecionado — os dias de fora só aparecem como número apagado no
     mini-calendário, sem badge (por isso não entram aqui). */
  const mesInicio = paraISO(new Date(mesRef.getFullYear(), mesRef.getMonth(), 1));
  const mesFim    = paraISO(new Date(mesRef.getFullYear(), mesRef.getMonth() + 1, 0));

  const porDia = {};
  itensFiltrados.forEach(it => {
    if (it.dueDate < mesInicio || it.dueDate > mesFim) return;
    if (!porDia[it.dueDate]) porDia[it.dueDate] = [];
    porDia[it.dueDate].push(it);
  });
  const datasComItens = Object.keys(porDia).sort();

  /* visão "Lista": um cliente abaixo do outro (sem agrupar por dia), ordenados
     pelo código; clicar abre todas as obrigações dele, ordenadas por vencimento.
     Com obrigação filtrada, começa já com TODOS os clientes vinculados a ela
     (mesmo sem item vencendo no mês visível) -- só depois soma os itens de
     quem tiver algo no período. */
  const porClienteLista = {};
  if (filtrosAplicados.obrigacaoId) {
    clientesDaObrigacao.forEach(c => {
      porClienteLista[c.id] = { nome: c.name, itens: [] };
    });
  }
  itensFiltrados.forEach(it => {
    if (it.dueDate < mesInicio || it.dueDate > mesFim) return;
    if (!mostrarConcluidos && it.concluido) return;
    const chave = it.clienteNiboId || it.cliente;
    if (!porClienteLista[chave]) porClienteLista[chave] = { nome: it.cliente, itens: [] };
    porClienteLista[chave].itens.push(it);
  });
  const gruposClienteLista = Object.values(porClienteLista).sort((a, b) =>
    codigoCliente(a.nome).localeCompare(codigoCliente(b.nome), 'pt-BR', { numeric: true })
  );

  function copiarListaClientes() {
    const texto = gruposClienteLista.map(g => g.nome).join('\n');
    navigator.clipboard.writeText(texto).then(() => {
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    });
  }

  function corBadge(diaStr) {
    if (diaStr < hojeStr) return 'red';
    if (diaStr === hojeStr) return 'yellow';
    return 'gray';
  }

  /* na visão Lista um cliente pode ter obrigações de vários dias misturadas —
     a cor do badge reflete a mais urgente entre as pendentes dele */
  function corGrupoCliente(itens) {
    const pendentes = itens.filter(i => !i.concluido || i.etapasPendentes);
    if (pendentes.length === 0) return 'green';
    if (pendentes.some(i => i.dueDate < hojeStr)) return 'red';
    if (pendentes.some(i => i.dueDate === hojeStr)) return 'yellow';
    return 'gray';
  }

  function trocarMes(delta) {
    setMesRef(m => new Date(m.getFullYear(), m.getMonth() + delta, 1));
  }

  function clicarDia(diaStr) {
    setDiaSelecionado(diaStr);
    gruposRef.current[diaStr]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  return (
    <>
      {dados.mock && <AvisoMock />}
      <div className={styles.calendarioLayout}>
        <div className={styles.calendarioCard}>
          <div className={styles.calendarioHeader} style={{ position: 'relative' }}>
            <span className={styles.calendarioTitulo}>Calendário</span>
            <div className={styles.calendarioNav}>
              <button type="button" className={styles.btnMes} onClick={() => trocarMes(-1)}><FiChevronLeft size={16} /></button>
              <span className={styles.calendarioMes}>{MESES[mesRef.getMonth()]}/{mesRef.getFullYear()}</span>
              <button type="button" className={styles.btnMes} onClick={() => trocarMes(1)}><FiChevronRight size={16} /></button>
              <button
                type="button"
                className={`${styles.btnFiltroCalendario} ${filtrosAtivos ? styles.btnFiltroCalendarioAtivo : ''}`}
                onClick={() => setFiltrosAbertos(v => !v)}
                title="Filtros"
              >
                <FiFilter size={14} />
              </button>
            </div>

            {filtrosAbertos && (
              <div style={{ position: 'fixed', inset: 0, zIndex: 998 }} onClick={() => setFiltrosAbertos(false)} />
            )}
            {filtrosAbertos && (
              <div className={styles.painelFiltrosCalendario} onClick={e => e.stopPropagation()}>
                <div className={styles.campoRoteiro}>
                  <label className={styles.campoLabel}>Cliente</label>
                  <input
                    className={styles.campoInput}
                    placeholder="Selecione um cliente..."
                    value={filtrosForm.clienteId ? filtrosForm.clienteNome : buscaCliente}
                    onChange={e => {
                      setBuscaCliente(e.target.value);
                      setFiltrosForm(f => ({ ...f, clienteId: null, clienteNome: '' }));
                    }}
                  />
                  {(buscandoCliente || clientesEncontrados.length > 0) && !filtrosForm.clienteId && buscaCliente.trim() && (
                    <ul className={styles.listaSugestoes}>
                      {buscandoCliente ? (
                        <li className={styles.muted}>Buscando...</li>
                      ) : clientesEncontrados.map(c => (
                        <li key={c.id} onClick={() => { setFiltrosForm(f => ({ ...f, clienteId: c.id, clienteNome: c.name })); setBuscaCliente(''); }}>
                          {c.name}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className={styles.campoRoteiro}>
                  <label className={styles.campoLabel}>Departamento</label>
                  <select
                    className={styles.campoInput}
                    value={filtrosForm.departamentoId}
                    onChange={e => setFiltrosForm(f => ({ ...f, departamentoId: e.target.value }))}
                  >
                    <option value="">Todos</option>
                    {departamentos.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                </div>

                <div className={styles.campoRoteiro}>
                  <label className={styles.campoLabel}>Obrigação</label>
                  <select
                    className={styles.campoInput}
                    value={filtrosForm.obrigacaoId}
                    onChange={e => {
                      const o = obrigacoesFiltro.find(x => x.id === e.target.value);
                      setFiltrosForm(f => ({ ...f, obrigacaoId: e.target.value, obrigacaoNome: o?.description || o?.nickname || '' }));
                    }}
                  >
                    <option value="">Todas</option>
                    {obrigacoesFiltro.map(o => (
                      <option key={o.id} value={o.id}>{o.description || o.nickname}</option>
                    ))}
                  </select>
                </div>

                <div className={styles.campoRoteiro}>
                  <label className={styles.campoLabel}>Responsável</label>
                  <select
                    className={styles.campoInput}
                    value={filtrosForm.responsavelNome}
                    onChange={e => setFiltrosForm(f => ({ ...f, responsavelNome: e.target.value }))}
                  >
                    <option value="">Todos</option>
                    {usuarios.map(u => <option key={u.id} value={u.name}>{u.name}</option>)}
                  </select>
                </div>

                <div className={styles.campoRoteiro}>
                  <label className={styles.campoLabel}>Datas exibidas</label>
                  <div style={{ display: 'flex', gap: 14, alignItems: 'center', paddingTop: 4 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, cursor: 'pointer' }}>
                      <input type="radio" checked={filtrosForm.baseData === 'interno'} onChange={() => setFiltrosForm(f => ({ ...f, baseData: 'interno' }))} />
                      Metas internas
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, cursor: 'pointer' }}>
                      <input type="radio" checked={filtrosForm.baseData === 'oficial'} onChange={() => setFiltrosForm(f => ({ ...f, baseData: 'oficial' }))} />
                      Vencimentos
                    </label>
                  </div>
                </div>

                <div className={styles.modalFooterForm}>
                  <button type="button" className={styles.btnCancelar} onClick={limparFiltros}>Limpar</button>
                  <button type="button" className={styles.btnSalvar} onClick={aplicarFiltros}>Filtrar</button>
                </div>
              </div>
            )}
          </div>

          {erro && <p className={styles.vazio}>{erro}</p>}

          <div className={styles.grade}>
            {DIAS_SEMANA.map(d => <div key={d} className={styles.gradeCabecalho}>{d}</div>)}
            {grade.map(d => {
              const diaStr = paraISO(d);
              const noMes  = d.getMonth() === mesRef.getMonth();
              const abertos = (porDia[diaStr] || []).filter(i => !i.concluido).length;
              return (
                <div
                  key={diaStr}
                  className={`${styles.gradeDia} ${!noMes ? styles.gradeDiaFora : ''} ${diaStr === hojeStr ? styles.gradeDiaHoje : ''} ${diaStr === diaSelecionado ? styles.gradeDiaSelecionado : ''}`}
                  onClick={() => porDia[diaStr] && clicarDia(diaStr)}
                >
                  <span className={styles.gradeNumero}>{d.getDate()}</span>
                  {noMes && abertos > 0 && (
                    <span className={`${styles.gradeBadge} ${styles['badge_' + corBadge(diaStr)]}`}>
                      {abertos > 99 ? '99+' : abertos}
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          <div className={styles.legenda}>
            <span className={styles.legendaTitulo}>Em aberto</span>
            <span className={styles.legendaItem}><i className={`${styles.legendaBola} ${styles.badge_red}`} /> Fora do prazo</span>
            <span className={styles.legendaItem}><i className={`${styles.legendaBola} ${styles.badge_yellow}`} /> Vence hoje</span>
            <span className={styles.legendaItem}><i className={`${styles.legendaBola} ${styles.badge_gray}`} /> Dentro do prazo</span>
          </div>
        </div>

        <div className={styles.metasCard}>
          <div className={styles.metasHeader}>
            <span className={styles.metasTitulo}>Metas internas</span>
            <div className={styles.metasAbas}>
              <button type="button" className={`${styles.metasAbaBtn} ${agrupamento === 'atividade' ? styles.metasAbaBtnAtiva : ''}`}
                onClick={() => setAgrupamento('atividade')}>Por atividade</button>
              <button type="button" className={`${styles.metasAbaBtn} ${agrupamento === 'cliente' ? styles.metasAbaBtnAtiva : ''}`}
                onClick={() => setAgrupamento('cliente')}>Por cliente</button>
              <button type="button" className={`${styles.metasAbaBtn} ${agrupamento === 'lista' ? styles.metasAbaBtnAtiva : ''}`}
                onClick={() => setAgrupamento('lista')}>Lista</button>
            </div>
            <label className={styles.toggleWrap}>
              <input type="checkbox" checked={mostrarConcluidos} onChange={e => setMostrarConcluidos(e.target.checked)} />
              <span className={styles.toggleTrilho}><span className={styles.toggleBola} /></span>
              Exibir concluídos
            </label>
          </div>

          <div className={styles.metasLista}>
            {carregando ? (
              <p className={styles.vazio}>Carregando...</p>
            ) : agrupamento === 'lista' ? (
              gruposClienteLista.length === 0 ? (
                <p className={styles.vazio}>Nenhum registro no período.</p>
              ) : (
                gruposClienteLista.map(grupo => {
                  const itensOrdenados = [...grupo.itens].sort((a, b) => (a.vencimentoOficial || '').localeCompare(b.vencimentoOficial || ''));
                  return (
                    <div
                      key={grupo.nome}
                      className={`${styles.metaGrupoLinha} ${styles.metaGrupoLinhaClicavel}`}
                      onClick={() => setGrupoDetalhe({ diaStr: null, nome: grupo.nome, itens: itensOrdenados })}
                    >
                      <span className={`${styles.metaGrupoBadge} ${styles['badge_' + corGrupoCliente(grupo.itens)]}`}>{grupo.itens.length}</span>
                      <span className={styles.metaGrupoNome}>{grupo.nome}</span>
                    </div>
                  );
                })
              )
            ) : datasComItens.length === 0 ? (
              <p className={styles.vazio}>Nenhum registro no período.</p>
            ) : (
              datasComItens.map(diaStr => {
                const itens = porDia[diaStr];
                /* "concluido" continua fiel ao status real da Nibo -- concluída
                   com etapa vinculada ainda pendente aparece como Concluído
                   (mostra a verdade do status), mas SEM entrar no toggle "Exibir
                   concluídos": fica sempre visível, com a etiqueta "Etapas
                   pendentes", até a etapa também ser marcada. */
                const pendentes  = itens.filter(i => !i.concluido);
                const concluidos = itens.filter(i => i.concluido);
                const concluidosPendentesDeEtapa = concluidos.filter(i => i.etapasPendentes);
                if (!mostrarConcluidos && pendentes.length === 0 && concluidosPendentesDeEtapa.length === 0) return null;

                const agrupar = lista => {
                  const porGrupo = {};
                  lista.forEach(i => {
                    const chave = agrupamento === 'atividade' ? i.obrigacao : i.cliente;
                    if (!porGrupo[chave]) porGrupo[chave] = { qtd: 0, obrigacaoId: i.obrigacaoId, origemInterna: i.origemInterna, etapasPendentes: false };
                    porGrupo[chave].qtd += 1;
                    /* se o grupo misturar tarefa interna com obrigação da Nibo (só
                       possível agrupando por cliente), não marca como "só tarefa" */
                    if (porGrupo[chave].origemInterna !== i.origemInterna) porGrupo[chave].origemInterna = false;
                    if (i.etapasPendentes) porGrupo[chave].etapasPendentes = true;
                  });
                  return Object.entries(porGrupo).sort((a, b) => b[1].qtd - a[1].qtd);
                };
                const gruposPendentes  = agrupar(pendentes);
                const gruposConcluidos = agrupar(mostrarConcluidos ? concluidos : concluidosPendentesDeEtapa);
                const cor = corBadge(diaStr);

                const abrirDetalhe = (nome, origem) => setGrupoDetalhe({
                  diaStr, nome,
                  itens: origem.filter(i => (agrupamento === 'atividade' ? i.obrigacao : i.cliente) === nome),
                });

                return (
                  <div key={diaStr} ref={el => { gruposRef.current[diaStr] = el; }}
                    className={`${styles.metaDia} ${diaStr === diaSelecionado ? styles.metaDiaSelecionada : ''}`}>
                    <div className={styles.metaDiaHeader}>
                      <span className={styles.metaDiaData}>{formatarDiaSemana(diaStr)}</span>
                      <span className={styles.metaDiaRatio}><FiCheck size={12} /> {concluidos.length}/{itens.length}</span>
                    </div>
                    {gruposPendentes.map(([nome, info]) => (
                      <div
                        key={nome}
                        className={`${styles.metaGrupoLinha} ${styles.metaGrupoLinhaClicavel}`}
                        onClick={() => abrirDetalhe(nome, pendentes)}
                      >
                        <span className={`${styles.metaGrupoBadge} ${styles['badge_' + cor]}`}>{info.qtd}</span>
                        <span className={styles.metaGrupoNome}>{nome}</span>
                        {info.origemInterna && <span className={styles.tagTarefa}>Tarefa</span>}
                        {info.etapasPendentes && (
                          <span className={styles.tagTarefa} style={{ background: '#fdecea', color: '#c0392b' }}>Etapas pendentes</span>
                        )}
                      </div>
                    ))}
                    {gruposConcluidos.length > 0 && (
                      <>
                        <p className={styles.metaConcluidoLabel}>Concluído</p>
                        {gruposConcluidos.map(([nome, info]) => (
                          <div
                            key={nome}
                            className={`${styles.metaGrupoLinha} ${styles.metaGrupoLinhaClicavel}`}
                            onClick={() => abrirDetalhe(nome, concluidos)}
                          >
                            <span className={`${styles.metaGrupoBadge} ${styles.badge_green}`}>{info.qtd}</span>
                            <span className={styles.metaGrupoNome}>{nome}</span>
                            {info.origemInterna && <span className={styles.tagTarefa}>Tarefa</span>}
                            {info.etapasPendentes && (
                              <span className={styles.tagTarefa} style={{ background: '#fdecea', color: '#c0392b' }}>Etapas pendentes</span>
                            )}
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {agrupamento === 'lista' && filtrosAplicados.obrigacaoId && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginTop: 12, paddingTop: 12, borderTop: '1px solid #e4edf5' }}>
              <span className={styles.muted} style={{ fontSize: 12 }}>
                Filtrando por obrigação: <strong>{filtrosAplicados.obrigacaoNome || '—'}</strong>
                {carregandoClientesObrigacao ? ' — carregando clientes...' : ` — ${gruposClienteLista.length} cliente${gruposClienteLista.length !== 1 ? 's' : ''}`}
              </span>
              <button
                type="button"
                className={styles.btnCancelar}
                disabled={gruposClienteLista.length === 0}
                onClick={copiarListaClientes}
              >
                {copiado ? 'Copiado!' : 'Copiar lista'}
              </button>
            </div>
          )}
        </div>
      </div>

      {grupoDetalhe && (
        <ModalGrupoCalendario
          grupo={grupoDetalhe}
          agrupamento={agrupamento}
          onClose={() => setGrupoDetalhe(null)}
          onConcluirTarefa={concluirTarefaCalendario}
          onReabrirTarefa={reabrirTarefaCalendario}
          onAlternarChecklistItem={alternarChecklistItemCalendario}
        />
      )}
    </>
  );
}

/* ── Modal de detalhe de um grupo do calendário — ao clicar numa obrigação
   (ou cliente) num dia, mostra a lista de clientes (ou obrigações) daquele
   grupo, igual ao comportamento do site da Nibo. ── */
function ModalGrupoCalendario({ grupo, agrupamento, onClose, onConcluirTarefa, onReabrirTarefa, onAlternarChecklistItem }) {
  const rotulo = agrupamento === 'atividade' ? 'cliente' : 'obrigação';
  const [itemExpandido, setItemExpandido] = useState(null);
  useFecharComEsc(onClose);
  return (
    <div className={styles.modalOverlay}>
      {/* tabela de 8 colunas (cliente/obrigação, tipo, competência, vencimento,
          responsável, status, ações) não cabe na largura padrão de 35% */}
      <div className={styles.modalBox} style={{ width: '60vw', minWidth: 700, maxWidth: 1100 }} onClick={e => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <span className={styles.modalTitulo}>{grupo.nome}</span>
          <button className={styles.modalBtnX} onClick={onClose}><FiX size={16} /></button>
        </div>
        <div className={styles.modalBody}>
          <p className={styles.modalSecaoTitulo}>
            {grupo.diaStr && `${formatarDiaSemana(grupo.diaStr)} — `}{grupo.itens.length} {rotulo}{grupo.itens.length !== 1 ? 's' : ''}
          </p>
          <div className={styles.tabelaWrap}>
            <table className={styles.tabela}>
              <thead>
                <tr>
                  <th>{agrupamento === 'atividade' ? 'Cliente' : 'Obrigação'}</th>
                  <th>Tipo</th>
                  <th>Competência</th>
                  <th>Vencimento</th>
                  <th>Responsável</th>
                  <th>Status</th>
                  <th></th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {grupo.itens.map((it, i) => {
                  const temDetalhe = it.origemInterna && ((it.checklist || []).length > 0 || it.observacoes);
                  const expandido = itemExpandido === i;
                  return (
                <React.Fragment key={i}>
                  <tr>
                    <td>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span>
                          {agrupamento === 'atividade' ? it.cliente : it.obrigacao}
                          {agrupamento === 'atividade' && it.clienteDocumento && (
                            <><br /><span className={styles.muted}>{it.clienteDocumento}</span></>
                          )}
                        </span>
                      </span>
                    </td>
                    <td>
                      <span className={it.origemInterna ? styles.tagTarefa : styles.tagObrigacao}>
                        {it.origemInterna ? 'Tarefa' : 'Obrigação'}{it.parcelaInfo ? ` ${it.parcelaInfo}` : ''}
                      </span>
                    </td>
                    <td>{formatarCompetencia(it.competencia)}</td>
                    <td>{formatarData(it.vencimentoOficial)}</td>
                    <td>{it.responsavel || '—'}</td>
                    <td>
                      <span className={`${styles.statusBadge} ${it.concluido ? styles.statusOk : styles.statusPendente}`}>
                        {it.concluido ? 'Concluído' : 'Pendente'}
                      </span>
                      {it.etapasPendentes && (
                        <span className={styles.tagTarefa} style={{ background: '#fdecea', color: '#c0392b', marginLeft: 6 }}>Etapas pendentes</span>
                      )}
                    </td>
                    <td>
                      {it.origemInterna && !it.concluido && (
                        <button
                          type="button"
                          className={styles.btnSalvar}
                          style={{ padding: '4px 10px', fontSize: 12 }}
                          onClick={() => onConcluirTarefa(it.tarefaId)}
                        >
                          Concluir
                        </button>
                      )}
                      {it.origemInterna && it.concluido && (
                        <button
                          type="button"
                          className={styles.btnRefresh}
                          style={{ padding: '4px 10px', fontSize: 12 }}
                          title="Reabrir tarefa concluída por engano"
                          onClick={() => onReabrirTarefa(it.tarefaId)}
                        >
                          Reabrir
                        </button>
                      )}
                    </td>
                    <td>
                      {temDetalhe && (
                        <button
                          type="button"
                          className={styles.btnRefresh}
                          onClick={() => setItemExpandido(expandido ? null : i)}
                          title="Detalhes"
                        >
                          {expandido ? <FiChevronUp size={12} /> : <FiChevronDown size={12} />}
                        </button>
                      )}
                    </td>
                  </tr>
                  {expandido && temDetalhe && (
                    <tr>
                      <td colSpan={8} style={{ background: '#fafcff' }}>
                        {it.observacoes && (
                          <p style={{ margin: '6px 0', fontSize: 13, whiteSpace: 'pre-wrap' }}>{it.observacoes}</p>
                        )}
                        {(it.checklist || []).map((item, idx) => (
                          <label key={item.id || idx} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0', fontSize: 13, cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={!!item.concluido}
                              onChange={() => onAlternarChecklistItem(it.tarefaId, idx)}
                            />
                            <span style={item.concluido ? { textDecoration: 'line-through', color: '#9ab0c4' } : undefined}>{item.texto}</span>
                          </label>
                        ))}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── espelho fiel do card "Metas internas" do Calendário (mesma listagem
   agrupada por atividade/cliente/lista), pra conferência cruzada na aba
   Auditoria -- sempre mês corrente, sem o grid/filtros do Calendário. ── */
function MetasInternasEspelho({
  mesRef, onTrocarMes, dados, carregando, erro, onRecarregar,
  idsClientesFiltro, setorFiltro, onFechar,
  clientes, onAbrirParticularidades, onAbrirFechamento,
  titulo = 'Metas internas',
}) {
  const [agrupamento, setAgrupamento]       = useState('atividade');
  const [mostrarConcluidos, setMostrarConcluidos] = useState(false);
  const [grupoDetalhe, setGrupoDetalhe]     = useState(null);
  const [usuarios, setUsuarios]             = useState([]);
  const [responsavelFiltro, setResponsavelFiltro] = useState('');

  /* Particularidades/Fechamento de cada cliente ficaram temporariamente
     aqui (na visão "Lista", que já agrupa por cliente) depois que a Grade
     de controle saiu -- ainda úteis, só precisam de um lugar definitivo
     mais pra frente. Resolve o registro da nossa base a partir do
     clienteNiboId de cada grupo. */
  const internoPorNiboId = useMemo(() => new Map(
    (clientes || []).filter(c => c.niboCustomerId).map(c => [c.niboCustomerId, c])
  ), [clientes]);

  useEffect(() => {
    listarUsuariosNiboAPI({ apenasAtivos: true }).then(r => setUsuarios(r.items || [])).catch(() => {});
  }, []);

  const hojeStr = paraISO(new Date());

  async function concluirTarefaCalendario(tarefaId) {
    try {
      await atualizarTarefa(tarefaId, { concluido: true });
      setGrupoDetalhe(null);
      onRecarregar();
    } catch (e) {
      console.error('[auditoria] falha ao concluir tarefa:', e.message);
    }
  }

  /* reabre uma tarefa interna marcada como concluída por engano */
  async function reabrirTarefaCalendario(tarefaId) {
    try {
      await atualizarTarefa(tarefaId, { concluido: false });
      setGrupoDetalhe(null);
      onRecarregar();
    } catch (e) {
      console.error('[auditoria] falha ao reabrir tarefa:', e.message);
    }
  }

  /* dados aqui é prop (dono é a aba Auditoria) -- só dá pra atualizar o
     grupo aberto localmente; onRecarregar (fire-and-forget) traz o dados
     do pai em dia caso o usuário reabra outro grupo depois. */
  async function alternarChecklistItemCalendario(tarefaId, indice) {
    const item = grupoDetalhe?.itens.find(it => it.tarefaId === tarefaId);
    if (!item) return;
    const novoChecklist = (item.checklist || []).map((ci, i) => i === indice ? { ...ci, concluido: !ci.concluido } : ci);
    setGrupoDetalhe(g => g && { ...g, itens: g.itens.map(it => it.tarefaId === tarefaId ? { ...it, checklist: novoChecklist } : it) });
    try {
      await atualizarTarefa(tarefaId, { checklist: novoChecklist });
    } catch (e) {
      console.error('[auditoria] falha ao atualizar checklist:', e.message);
      onRecarregar();
    }
  }

  const itensFiltrados = (dados.items || [])
    .filter(it => !idsClientesFiltro || idsClientesFiltro.length === 0 || idsClientesFiltro.includes(it.clienteNiboId))
    .filter(it => !setorFiltro || setorFiltro === 'todos' || setorDeItem(it) === setorFiltro)
    .filter(it => !responsavelFiltro || it.responsavel === responsavelFiltro);

  const mesInicio = paraISO(new Date(mesRef.getFullYear(), mesRef.getMonth(), 1));
  const mesFim    = paraISO(new Date(mesRef.getFullYear(), mesRef.getMonth() + 1, 0));

  const porDia = {};
  itensFiltrados.forEach(it => {
    if (it.dueDate < mesInicio || it.dueDate > mesFim) return;
    if (!porDia[it.dueDate]) porDia[it.dueDate] = [];
    porDia[it.dueDate].push(it);
  });
  const datasComItens = Object.keys(porDia).sort();

  const porClienteLista = {};
  itensFiltrados.forEach(it => {
    if (it.dueDate < mesInicio || it.dueDate > mesFim) return;
    if (!mostrarConcluidos && it.concluido) return;
    const chave = it.clienteNiboId || it.cliente;
    if (!porClienteLista[chave]) porClienteLista[chave] = { nome: it.cliente, clienteNiboId: it.clienteNiboId, itens: [] };
    porClienteLista[chave].itens.push(it);
  });
  const gruposClienteLista = Object.values(porClienteLista).sort((a, b) =>
    codigoCliente(a.nome).localeCompare(codigoCliente(b.nome), 'pt-BR', { numeric: true })
  );

  function corBadge(diaStr) {
    if (diaStr < hojeStr) return 'red';
    if (diaStr === hojeStr) return 'yellow';
    return 'gray';
  }

  function corGrupoCliente(itens) {
    const pendentes = itens.filter(i => !i.concluido || i.etapasPendentes);
    if (pendentes.length === 0) return 'green';
    if (pendentes.some(i => i.dueDate < hojeStr)) return 'red';
    if (pendentes.some(i => i.dueDate === hojeStr)) return 'yellow';
    return 'gray';
  }

  return (
    <>
      {dados.mock && <AvisoMock />}
      <div className={styles.metasCard}>
        <div className={styles.metasHeader}>
          <span className={styles.metasTitulo}>{titulo}</span>
          <div className={styles.calendarioNav}>
            <button type="button" className={styles.btnMes} onClick={() => onTrocarMes(-1)}><FiChevronLeft size={16} /></button>
            <span className={styles.calendarioMes}>{MESES[mesRef.getMonth()]}/{mesRef.getFullYear()}</span>
            <button type="button" className={styles.btnMes} onClick={() => onTrocarMes(1)}><FiChevronRight size={16} /></button>
            <select
              className={styles.campoInput}
              style={{ width: 'auto', fontSize: 12, padding: '4px 8px', marginLeft: 6 }}
              value={responsavelFiltro}
              onChange={e => setResponsavelFiltro(e.target.value)}
              title="Filtrar por responsável"
            >
              <option value="">Todos os responsáveis</option>
              {usuarios.map(u => <option key={u.id} value={u.name}>{u.name}</option>)}
            </select>
          </div>
          <div className={styles.metasAbas}>
            <button type="button" className={`${styles.metasAbaBtn} ${agrupamento === 'atividade' ? styles.metasAbaBtnAtiva : ''}`}
              onClick={() => setAgrupamento('atividade')}>Por atividade</button>
            <button type="button" className={`${styles.metasAbaBtn} ${agrupamento === 'cliente' ? styles.metasAbaBtnAtiva : ''}`}
              onClick={() => setAgrupamento('cliente')}>Por cliente</button>
            <button type="button" className={`${styles.metasAbaBtn} ${agrupamento === 'lista' ? styles.metasAbaBtnAtiva : ''}`}
              onClick={() => setAgrupamento('lista')}>Lista</button>
          </div>
          <label className={styles.toggleWrap}>
            <input type="checkbox" checked={mostrarConcluidos} onChange={e => setMostrarConcluidos(e.target.checked)} />
            <span className={styles.toggleTrilho}><span className={styles.toggleBola} /></span>
            Exibir concluídos
          </label>
          {onFechar && (
            <button type="button" title="Fechar" onClick={onFechar} className={styles.metasFechar}>
              <FiX size={16} />
            </button>
          )}
        </div>

        <div className={styles.metasLista}>
          {carregando ? (
            <p className={styles.vazio}>Carregando...</p>
          ) : erro ? (
            <p className={styles.vazio}>{erro}</p>
          ) : agrupamento === 'lista' ? (
            gruposClienteLista.length === 0 ? (
              <p className={styles.vazio}>Nenhum registro no período.</p>
            ) : (
              gruposClienteLista.map(grupo => {
                const itensOrdenados = [...grupo.itens].sort((a, b) => (a.vencimentoOficial || '').localeCompare(b.vencimentoOficial || ''));
                const clienteInterno = grupo.clienteNiboId ? internoPorNiboId.get(grupo.clienteNiboId) : null;
                return (
                  <div
                    key={grupo.nome}
                    className={`${styles.metaGrupoLinha} ${styles.metaGrupoLinhaClicavel}`}
                    onClick={() => setGrupoDetalhe({ diaStr: null, nome: grupo.nome, itens: itensOrdenados })}
                  >
                    <span className={`${styles.metaGrupoBadge} ${styles['badge_' + corGrupoCliente(grupo.itens)]}`}>{grupo.itens.length}</span>
                    <span className={styles.metaGrupoNome}>{grupo.nome}</span>
                    {clienteInterno && (
                      <span style={{ display: 'flex', gap: 3, marginLeft: 'auto' }}>
                        <button
                          type="button"
                          title="Particularidades"
                          onClick={e => { e.stopPropagation(); onAbrirParticularidades(clienteInterno.id); }}
                          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 20, height: 20, border: 'none', borderRadius: '50%', background: '#eef4fb', color: '#4A6B8A', cursor: 'pointer', flexShrink: 0 }}
                        >
                          <FiInfo size={11} />
                        </button>
                        <button
                          type="button"
                          title="Fechamento"
                          onClick={e => { e.stopPropagation(); onAbrirFechamento(clienteInterno.id); }}
                          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 20, height: 20, border: 'none', borderRadius: '50%', background: '#eef4fb', color: '#4A6B8A', cursor: 'pointer', flexShrink: 0 }}
                        >
                          <FiCheckSquare size={11} />
                        </button>
                      </span>
                    )}
                  </div>
                );
              })
            )
          ) : datasComItens.length === 0 ? (
            <p className={styles.vazio}>Nenhum registro no período.</p>
          ) : (
            datasComItens.map(diaStr => {
              const itens = porDia[diaStr];
              /* "concluido" continua fiel ao status real da Nibo -- concluída
                 com etapa vinculada ainda pendente aparece como Concluído
                 (mostra a verdade do status), mas SEM entrar no toggle "Exibir
                 concluídos": fica sempre visível, com a etiqueta "Etapas
                 pendentes", até a etapa também ser marcada. */
              const pendentes  = itens.filter(i => !i.concluido);
              const concluidos = itens.filter(i => i.concluido);
              const concluidosPendentesDeEtapa = concluidos.filter(i => i.etapasPendentes);
              if (!mostrarConcluidos && pendentes.length === 0 && concluidosPendentesDeEtapa.length === 0) return null;

              const agrupar = lista => {
                const porGrupo = {};
                lista.forEach(i => {
                  const chave = agrupamento === 'atividade' ? i.obrigacao : i.cliente;
                  if (!porGrupo[chave]) porGrupo[chave] = { qtd: 0, obrigacaoId: i.obrigacaoId, origemInterna: i.origemInterna, clienteNiboId: i.clienteNiboId, clienteNome: i.cliente, etapasPendentes: false };
                  porGrupo[chave].qtd += 1;
                  if (porGrupo[chave].origemInterna !== i.origemInterna) porGrupo[chave].origemInterna = false;
                  if (i.etapasPendentes) porGrupo[chave].etapasPendentes = true;
                });
                return Object.entries(porGrupo).sort((a, b) => b[1].qtd - a[1].qtd);
              };
              const gruposPendentes  = agrupar(pendentes);
              const gruposConcluidos = agrupar(mostrarConcluidos ? concluidos : concluidosPendentesDeEtapa);
              const cor = corBadge(diaStr);

              const abrirDetalhe = (nome, origem) => setGrupoDetalhe({
                diaStr, nome,
                itens: origem.filter(i => (agrupamento === 'atividade' ? i.obrigacao : i.cliente) === nome),
              });

              return (
                <div key={diaStr} className={styles.metaDia}>
                  <div className={styles.metaDiaHeader}>
                    <span className={styles.metaDiaData}>{formatarDiaSemana(diaStr)}</span>
                    <span className={styles.metaDiaRatio}><FiCheck size={12} /> {concluidos.length}/{itens.length}</span>
                  </div>
                  {gruposPendentes.map(([nome, info]) => (
                    <div
                      key={nome}
                      className={`${styles.metaGrupoLinha} ${styles.metaGrupoLinhaClicavel}`}
                      onClick={() => abrirDetalhe(nome, pendentes)}
                    >
                      <span className={`${styles.metaGrupoBadge} ${styles['badge_' + cor]}`}>{info.qtd}</span>
                      <span className={styles.metaGrupoNome}>{nome}</span>
                      {info.origemInterna && <span className={styles.tagTarefa}>Tarefa</span>}
                      {info.etapasPendentes && (
                        <span className={styles.tagTarefa} style={{ background: '#fdecea', color: '#c0392b' }}>Etapas pendentes</span>
                      )}
                    </div>
                  ))}
                  {gruposConcluidos.length > 0 && (
                    <>
                      <p className={styles.metaConcluidoLabel}>Concluído</p>
                      {gruposConcluidos.map(([nome, info]) => (
                        <div
                          key={nome}
                          className={`${styles.metaGrupoLinha} ${styles.metaGrupoLinhaClicavel}`}
                          onClick={() => abrirDetalhe(nome, concluidos)}
                        >
                          <span className={`${styles.metaGrupoBadge} ${styles.badge_green}`}>{info.qtd}</span>
                          <span className={styles.metaGrupoNome}>{nome}</span>
                          {info.origemInterna && <span className={styles.tagTarefa}>Tarefa</span>}
                          {info.etapasPendentes && (
                            <span className={styles.tagTarefa} style={{ background: '#fdecea', color: '#c0392b' }}>Etapas pendentes</span>
                          )}
                        </div>
                      ))}
                    </>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {grupoDetalhe && (
        <ModalGrupoCalendario
          grupo={grupoDetalhe}
          agrupamento={agrupamento}
          onClose={() => setGrupoDetalhe(null)}
          onConcluirTarefa={concluirTarefaCalendario}
          onReabrirTarefa={reabrirTarefaCalendario}
          onAlternarChecklistItem={alternarChecklistItemCalendario}
        />
      )}
    </>
  );
}

/* ── gerenciador de perguntas de fechamento de UM setor, de UM cliente --
   fica dentro de Particularidades (revelado pelo botão "+ Perguntas de
   fechamento" enquanto o cliente está em edição). Lista todas (ativas e
   inativas, riscadas) com um checkbox pra ativar/desativar, e um campo
   pra cadastrar uma nova -- mesmo comportamento que existia em
   Configurações antes de virar por-cliente, só que relocado. ── */
function GerenciadorPerguntasSetor({ setor, perguntas, onAdicionar, onAlternarAtivo }) {
  const [novoTexto, setNovoTexto] = useState('');
  const [salvando, setSalvando]   = useState(false);

  async function handleAdicionar() {
    if (!novoTexto.trim() || salvando) return;
    setSalvando(true);
    try {
      await onAdicionar(novoTexto);
      setNovoTexto('');
    } catch (e) {
      console.error('[auditoria] falha ao criar pergunta de fechamento:', e.message);
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div style={{ background: '#f7fafd', borderRadius: 8, padding: '8px 10px' }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#0D1F3C', marginBottom: 6 }}>{setor.label}</div>
      {perguntas.length === 0 && (
        <div style={{ fontSize: 11, color: '#9ab0c4', marginBottom: 6 }}>Nenhuma pergunta cadastrada.</div>
      )}
      {perguntas.map(p => (
        <label
          key={p.id}
          style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, padding: '3px 0', cursor: 'pointer', color: p.ativo ? '#9ab0c4' : '#0D1F3C', textDecoration: p.ativo ? 'line-through' : 'none' }}
        >
          <input type="checkbox" checked={p.ativo} onChange={() => onAlternarAtivo(p)} />
          {p.texto}
        </label>
      ))}
      <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
        <input
          type="text"
          value={novoTexto}
          onChange={e => setNovoTexto(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAdicionar(); } }}
          placeholder="Nova pergunta..."
          style={{ flex: 1, fontSize: 12, padding: '4px 6px', border: '1px solid #d8e3ee', borderRadius: 5, fontFamily: 'inherit' }}
        />
        <button
          type="button"
          onClick={handleAdicionar}
          disabled={salvando || !novoTexto.trim()}
          style={{ fontSize: 11, background: '#0D1F3C', color: '#fff', border: 'none', borderRadius: 5, padding: '4px 10px', cursor: salvando || !novoTexto.trim() ? 'default' : 'pointer', opacity: salvando || !novoTexto.trim() ? 0.5 : 1, fontFamily: 'inherit' }}
        >
          Adicionar
        </button>
      </div>
    </div>
  );
}

/* ── Modal de Fechamento: etapas + perguntas de fechamento de UM cliente --
   antes tinha aqui também o botão "Fechar Fiscal/Folha/Contábil" (status
   manual por setor); esse mecanismo saiu -- agora "concluir" um cliente é
   automático, derivado de GradeControle (todas as colunas vinculadas
   marcadas + obrigação da Nibo concluída, ver clienteConcluido em
   AuditoriaTab), sem botão nenhum pra clicar aqui.
   "clienteInterno" é sempre o registro da nossa base; "clienteNiboId" é o
   vínculo (nibo_customer_id) usado só pra casar com obrigações/etapas da
   Nibo -- fica null se esse cliente ainda não foi vinculado, e a seção de
   Etapas some nesse caso. ── */
function ModalFechamento({ clienteNiboId, clienteInterno, nomeCliente, mesRef, setorFiltro, dados, etapasPorObrigacao, onClose }) {
  const [anexoAberto, setAnexoAberto] = useState(null);

  useFecharComEsc(onClose);

  const competencia = `${mesRef.getFullYear()}-${String(mesRef.getMonth() + 1).padStart(2, '0')}`;
  const mesInicio = paraISO(new Date(mesRef.getFullYear(), mesRef.getMonth(), 1));
  const mesFim    = paraISO(new Date(mesRef.getFullYear(), mesRef.getMonth() + 1, 0));
  const itensCliente = (dados.items || []).filter(it =>
    it.clienteNiboId === clienteNiboId &&
    it.dueDate >= mesInicio && it.dueDate <= mesFim
  );

  const [perguntas, setPerguntas]               = useState([]);
  const [gerenciarPerguntas, setGerenciarPerguntas] = useState(false);
  const [conclusoesEtapas, setConclusoesEtapas] = useState([]);
  const [etapasExpandidas, setEtapasExpandidas] = useState(new Set());

  function alternarExpansaoEtapa(etapaId) {
    setEtapasExpandidas(prev => {
      const novo = new Set(prev);
      if (novo.has(etapaId)) novo.delete(etapaId); else novo.add(etapaId);
      return novo;
    });
  }

  useEffect(() => {
    listarConclusoesEtapas(clienteNiboId, competencia)
      .then(r => setConclusoesEtapas(r.items || []))
      .catch(() => setConclusoesEtapas([]));
  }, [clienteNiboId, competencia]);

  /* uma etapa por obrigação vinculada que esse cliente tem no mês, sem
     repetir (uma obrigação pode ter mais de um vencimento no período).
     Segue o mesmo filtro de setor (Fiscal/Folha/Contábil/Todos) da tela. */
  const etapasDoCliente = Array.from(
    new Map(
      itensCliente
        .filter(it => it.obrigacaoId && etapasPorObrigacao?.has(it.obrigacaoId))
        .filter(it => !setorFiltro || setorFiltro === 'todos' || setorDeItem(it) === setorFiltro)
        .map(it => etapasPorObrigacao.get(it.obrigacaoId))
        .map(etapa => [etapa.id, etapa])
    ).values()
  );

  async function alternarEtapaSubitem(etapaId, subitemId, concluido) {
    setConclusoesEtapas(prev => concluido
      ? [...prev.filter(c => !(c.etapa_padrao_id === etapaId && c.subitem_id === subitemId)), { etapa_padrao_id: etapaId, subitem_id: subitemId }]
      : prev.filter(c => !(c.etapa_padrao_id === etapaId && c.subitem_id === subitemId)));
    try {
      await alternarConclusaoSubitem(etapaId, subitemId, clienteNiboId, competencia, concluido);
    } catch (e) {
      console.error('[auditoria] falha ao atualizar etapa:', e.message);
      setConclusoesEtapas(prev => concluido
        ? prev.filter(c => !(c.etapa_padrao_id === etapaId && c.subitem_id === subitemId))
        : [...prev, { etapa_padrao_id: etapaId, subitem_id: subitemId }]);
    }
  }

  /* perguntas são cadastradas por ESSE cliente -- busca com inativas
     incluídas pra dar pra reativar uma antiga no gerenciador. */
  useEffect(() => {
    if (!clienteInterno) { setPerguntas([]); return; }
    listarPerguntasFechamento({ clienteId: clienteInterno.id, incluirInativas: true })
      .then(ps => setPerguntas(ps.items || []))
      .catch(() => setPerguntas([]));
  }, [clienteInterno, competencia]);

  async function adicionarPergunta(setorKey, texto) {
    if (!clienteInterno || !texto.trim()) return;
    const criada = await criarPerguntaFechamento(clienteInterno.id, setorKey, texto.trim());
    setPerguntas(ps => [...ps, criada]);
  }

  async function alternarAtivoPergunta(pergunta) {
    const atualizada = await atualizarPerguntaFechamento(pergunta.id, { ativo: !pergunta.ativo });
    setPerguntas(ps => ps.map(p => p.id === pergunta.id ? atualizada : p));
  }

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalBox} onClick={e => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <span className={styles.modalTitulo}>{nomeCliente}</span>
          <button className={styles.modalBtnX} onClick={onClose}><FiX size={16} /></button>
        </div>
        <div className={styles.modalBody}>
      <div style={{ marginBottom: 14 }}>
        {clienteInterno && (
          <button
            type="button"
            onClick={() => setGerenciarPerguntas(v => !v)}
            style={{ fontSize: 11, fontWeight: 600, background: '#eef4fb', border: 'none', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', color: '#0D1F3C', fontFamily: 'inherit' }}
          >
            {gerenciarPerguntas ? 'Ocultar perguntas de fechamento' : '+ Perguntas de fechamento'}
          </button>
        )}

        {clienteInterno && gerenciarPerguntas && (
          <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {SETORES.map(s => (
              <GerenciadorPerguntasSetor
                key={s.key}
                setor={s}
                perguntas={perguntas.filter(p => p.setor === s.key)}
                onAdicionar={texto2 => adicionarPergunta(s.key, texto2)}
                onAlternarAtivo={alternarAtivoPergunta}
              />
            ))}
          </div>
        )}
      </div>

      {etapasDoCliente.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <div className={styles.muted} style={{ fontSize: 11, marginBottom: 4 }}>Etapas</div>
          {etapasDoCliente.map(etapa => {
            const concluidosIds = new Set(
              conclusoesEtapas.filter(c => c.etapa_padrao_id === etapa.id).map(c => c.subitem_id)
            );
            const folhas = (etapa.itens || []).flatMap(it => [it.id, ...(it.subitens || []).map(s => s.id)]);
            const feitos = folhas.filter(id => concluidosIds.has(id)).length;
            const expandida = etapasExpandidas.has(etapa.id);
            return (
              <div key={etapa.id} style={{ marginBottom: 4 }}>
                <button
                  type="button"
                  onClick={() => alternarExpansaoEtapa(etapa.id)}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%',
                    textAlign: 'left', background: '#eef4fb', border: 'none', borderRadius: 6, padding: '4px 8px',
                    cursor: 'pointer', fontSize: 13, fontWeight: 700, color: '#0D1F3C', fontFamily: 'inherit',
                  }}
                >
                  <span>{etapa.titulo} <span style={{ fontSize: 11, fontWeight: 400, color: '#6B8CAE' }}>· {feitos}/{folhas.length}</span></span>
                  {expandida ? <FiChevronUp size={13} /> : <FiChevronDown size={13} />}
                </button>
                {expandida && (
                  /* grid de 2 colunas (texto | toggle) -- a 1ª coluna acompanha o
                     texto mais longo do grupo inteiro (item ou subitem), então
                     todo toggle alinha na mesma posição em vez de ficar colado
                     logo depois de cada texto, que tem tamanhos diferentes */
                  <div className={styles.toggleEtapaGrupo} style={{ marginTop: 4, padding: '0 4px' }}>
                    {(etapa.itens || []).flatMap(item => {
                      const itemMarcado = concluidosIds.has(item.id);
                      /* o item é uma confirmação própria (ex.: "Fechamento"), mesmo
                         tendo subitens -- cada subitem (ex.: "Transmissão DCTFWeb")
                         é outra validação independente, com seu próprio toggle */
                      const linhaItem = (
                        <label key={item.id} className={styles.toggleEtapaWrap}>
                          <span style={{ fontSize: 12, fontWeight: 600, cursor: 'pointer', ...(itemMarcado ? { textDecoration: 'line-through', color: '#9ab0c4' } : { color: '#0D1F3C' }) }}>
                            {item.texto}
                          </span>
                          <input
                            type="checkbox"
                            checked={itemMarcado}
                            onChange={() => alternarEtapaSubitem(etapa.id, item.id, !itemMarcado)}
                          />
                          <span className={styles.toggleEtapaTrilho}><span className={styles.toggleEtapaBola} /></span>
                        </label>
                      );
                      const linhasSubitens = (item.subitens || []).map(sub => {
                        const marcado = concluidosIds.has(sub.id);
                        return (
                          <label key={sub.id} className={styles.toggleEtapaWrap}>
                            <span style={{ fontSize: 12, paddingLeft: 12, cursor: 'pointer', ...(marcado ? { textDecoration: 'line-through', color: '#9ab0c4' } : {}) }}>
                              {sub.texto}
                            </span>
                            <input type="checkbox" checked={marcado} onChange={() => alternarEtapaSubitem(etapa.id, sub.id, !marcado)} />
                            <span className={styles.toggleEtapaTrilho}><span className={styles.toggleEtapaBola} /></span>
                          </label>
                        );
                      });
                      return [linhaItem, ...linhasSubitens];
                    })}
                  </div>
                )}
                {expandida && (etapa.anexos || []).length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6, padding: '0 4px' }}>
                    {etapa.anexos.map(anexo => (
                      <button
                        key={anexo.id}
                        type="button"
                        onClick={() => setAnexoAberto(anexo)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 5, background: '#fff',
                          border: '1px solid #d0dde8', borderRadius: 20, padding: '3px 10px',
                          fontSize: 11, color: '#4A6B8A', cursor: 'pointer', fontFamily: 'inherit',
                        }}
                        title={anexo.url}
                      >
                        <FiPaperclip size={11} /> {anexo.nome}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {anexoAberto && (
        <AnexoEtapaViewerModal anexo={anexoAberto} onClose={() => setAnexoAberto(null)} />
      )}
        </div>
      </div>
    </div>
  );
}

/* link de compartilhamento do Google Drive → URL de embed (.../preview),
   que o Drive permite mostrar dentro de iframe de outro site */
function driveEmbedUrl(url) {
  const pasta = url.match(/\/drive\/folders\/([a-zA-Z0-9_-]+)/) || url.match(/\/drive\/u\/\d+\/folders\/([a-zA-Z0-9_-]+)/);
  if (pasta) return `https://drive.google.com/embeddedfolderview?id=${pasta[1]}#list`;
  const arquivo = url.match(/\/file\/d\/([a-zA-Z0-9_-]+)/) || url.match(/[?&]id=([a-zA-Z0-9_-]+)/);
  return arquivo ? `https://drive.google.com/file/d/${arquivo[1]}/preview` : null;
}

/* ── visualizador interno de anexo de uma etapa — nunca sai do sistema.
   Google Drive vira iframe de preview; imagem/vídeo/pdf direto pela URL
   também renderiza inline; qualquer outra coisa cai num botão de abrir em
   nova aba, mas dentro do mesmo modal (nunca navega direto ao clicar). ── */
function AnexoEtapaViewerModal({ anexo, onClose }) {
  const driveUrl = driveEmbedUrl(anexo.url);
  const isImagem = /\.(jpe?g|png|gif|webp|svg)(\?|$)/i.test(anexo.url);
  const isVideo  = /\.(mp4|webm|ogg|mov)(\?|$)/i.test(anexo.url);
  const isPdf    = /\.pdf(\?|$)/i.test(anexo.url);
  useFecharComEsc(onClose);

  return (
    <div className={styles.modalOverlay} style={{ zIndex: 10500 }}>
      <div className={styles.modalBox} style={{ width: '60vw', minWidth: 640, maxWidth: 1200 }} onClick={e => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <span className={styles.modalTitulo}>{anexo.nome}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <a href={anexo.url} target="_blank" rel="noopener noreferrer" className={styles.btnRoteiroIcone} title="Abrir em nova aba">
              <FiExternalLink size={15} />
            </a>
            <button className={styles.modalBtnX} onClick={onClose}><FiX size={16} /></button>
          </div>
        </div>
        <div className={styles.modalBody} style={{ padding: 0, height: 'calc(100vh - 56px)' }}>
          {driveUrl ? (
            <iframe src={driveUrl} title={anexo.nome} style={{ width: '100%', height: '100%', border: 'none', display: 'block' }} allow="autoplay" />
          ) : isImagem ? (
            <img src={anexo.url} alt={anexo.nome} style={{ width: '100%', display: 'block' }} />
          ) : isVideo ? (
            <video src={anexo.url} controls style={{ width: '100%', display: 'block', maxHeight: '100%' }} />
          ) : isPdf ? (
            <iframe src={anexo.url} title={anexo.nome} style={{ width: '100%', height: '100%', border: 'none', display: 'block' }} />
          ) : (
            <div style={{ padding: 32, textAlign: 'center' }}>
              <p className={styles.vazio}>Não foi possível pré-visualizar este material aqui.</p>
              <a href={anexo.url} target="_blank" rel="noopener noreferrer" className={styles.btnSalvar} style={{ display: 'inline-block', textDecoration: 'none' }}>
                Abrir em nova aba
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Modal de Particularidades: texto livre + seções + links nomeados de UM
   cliente -- antes ficava fixo dentro do card expandido, agora abre pelo
   ícone antes do código na lista, sem precisar expandir o cliente inteiro. */
function ModalParticularidades({ clienteInterno, nomeCliente, onClienteAtualizado, onClose }) {
  const [texto, setTexto]       = useState(clienteInterno?.particularidades || '');
  const [editando, setEditando] = useState(false);
  const textareaRef             = useRef(null);
  const valorAntesEditar        = useRef('');

  /* seções (título + texto) e links nomeados -- diferente do texto livre
     acima (legado, mantido pra não perder o que já tinha sido digitado),
     aqui cada bloco é salvo separado, com uma linha discreta entre um e
     outro; mais fácil de organizar informação por assunto. */
  const [secoes, setSecoes]   = useState(clienteInterno?.particularidadesSecoes || []);
  const [links, setLinks]     = useState(clienteInterno?.particularidadesLinks || []);
  const [formSecao, setFormSecao] = useState(null); // null = fechado; { id: null|existente, titulo, texto }
  const [formLink, setFormLink]   = useState(null); // null = fechado; { nome, url }

  useFecharComEsc(onClose);

  useEffect(() => {
    setTexto(clienteInterno?.particularidades || '');
    setSecoes(clienteInterno?.particularidadesSecoes || []);
    setLinks(clienteInterno?.particularidadesLinks || []);
  }, [clienteInterno?.id, clienteInterno?.particularidades, clienteInterno?.particularidadesSecoes, clienteInterno?.particularidadesLinks]);

  useEffect(() => {
    if (editando && textareaRef.current) {
      const el = textareaRef.current;
      el.focus();
      el.setSelectionRange(el.value.length, el.value.length);
    }
  }, [editando]);

  async function salvarSecoes(novasSecoes) {
    setSecoes(novasSecoes);
    if (!clienteInterno) return;
    try {
      const atualizado = await atualizarNossoCliente(clienteInterno.id, { particularidades_secoes: novasSecoes });
      onClienteAtualizado(atualizado);
    } catch (e) {
      console.error('[auditoria] falha ao salvar seções de particularidades:', e.message);
    }
  }

  function abrirNovaSecao() { setFormSecao({ id: null, titulo: '', texto: '' }); }
  function abrirEdicaoSecao(secao) { setFormSecao({ id: secao.id, titulo: secao.titulo, texto: secao.texto }); }
  function cancelarFormSecao() { setFormSecao(null); }
  function confirmarFormSecao() {
    if (!formSecao.titulo.trim()) return;
    const salva = {
      id: formSecao.id || (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`),
      titulo: formSecao.titulo.trim(),
      texto: formSecao.texto,
    };
    const novas = formSecao.id ? secoes.map(s => s.id === formSecao.id ? salva : s) : [...secoes, salva];
    salvarSecoes(novas);
    setFormSecao(null);
  }
  function removerSecao(id) {
    if (!window.confirm('Remover esta seção?')) return;
    salvarSecoes(secoes.filter(s => s.id !== id));
  }

  async function salvarLinks(novosLinks) {
    setLinks(novosLinks);
    if (!clienteInterno) return;
    try {
      const atualizado = await atualizarNossoCliente(clienteInterno.id, { particularidades_links: novosLinks });
      onClienteAtualizado(atualizado);
    } catch (e) {
      console.error('[auditoria] falha ao salvar links de particularidades:', e.message);
    }
  }

  function abrirNovoLink() { setFormLink({ nome: '', url: '' }); }
  function cancelarFormLink() { setFormLink(null); }
  function confirmarFormLink() {
    if (!formLink.nome.trim() || !formLink.url.trim()) return;
    const novo = {
      id: crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`,
      nome: formLink.nome.trim(),
      url: formLink.url.trim(),
    };
    salvarLinks([...links, novo]);
    setFormLink(null);
  }
  function removerLink(id) { salvarLinks(links.filter(l => l.id !== id)); }

  function iniciarEdicao() {
    valorAntesEditar.current = texto;
    setEditando(true);
  }

  async function salvarParticularidades() {
    setEditando(false);
    if (!clienteInterno || texto === valorAntesEditar.current) return;
    try {
      const atualizado = await atualizarNossoCliente(clienteInterno.id, { particularidades: texto });
      onClienteAtualizado(atualizado);
    } catch {
      setTexto(valorAntesEditar.current); // falhou salvar -- volta pro que tinha antes
    }
  }

  function cancelarEdicao() {
    setTexto(valorAntesEditar.current);
    setEditando(false);
  }

  function aoTeclarTextarea(e) {
    if (e.key === 'Escape') { e.preventDefault(); cancelarEdicao(); }
    else if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') { e.preventDefault(); salvarParticularidades(); }
  }

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalBox} onClick={e => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <span className={styles.modalTitulo}>{nomeCliente}</span>
          <button className={styles.modalBtnX} onClick={onClose}><FiX size={16} /></button>
        </div>
        <div className={styles.modalBody}>
          <div className={styles.muted} style={{ fontSize: 11, marginBottom: 4 }}>Particularidades</div>
          {!clienteInterno ? (
            <p className={styles.muted} style={{ fontSize: 12, margin: 0 }}>
              Cliente ainda não cadastrado na nossa base — cadastre em Clientes pra habilitar particularidades e fechamento por setor.
            </p>
          ) : editando ? (
            <textarea
              ref={textareaRef}
              className={styles.campoTextarea}
              value={texto}
              onChange={e => setTexto(e.target.value)}
              onBlur={salvarParticularidades}
              onKeyDown={aoTeclarTextarea}
              rows={3}
            />
          ) : (
            <p
              onDoubleClick={iniciarEdicao}
              style={{ fontSize: 13, whiteSpace: 'pre-wrap', cursor: 'text', margin: 0, color: texto ? '#0D1F3C' : '#9ab0c4' }}
            >
              {texto || 'Clique duas vezes para adicionar particularidades deste cliente.'}
            </p>
          )}

          {clienteInterno && secoes.length > 0 && (
            <div style={{ marginTop: 10 }}>
              {secoes.map((s, i) => (
                <div key={s.id} style={{ paddingTop: i === 0 ? 0 : 8, marginTop: i === 0 ? 0 : 8, borderTop: i === 0 ? 'none' : '1px solid #eef2f7' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                    <strong style={{ fontSize: 12, color: '#0D1F3C' }}>{s.titulo}</strong>
                    <span style={{ display: 'flex', gap: 2 }}>
                      <button type="button" onClick={() => abrirEdicaoSecao(s)} title="Editar seção" style={{ display: 'flex', border: 'none', background: 'none', cursor: 'pointer', color: '#9ab0c4', padding: 2 }}>
                        <FiEdit2 size={11} />
                      </button>
                      <button type="button" onClick={() => removerSecao(s.id)} title="Remover seção" style={{ display: 'flex', border: 'none', background: 'none', cursor: 'pointer', color: '#9ab0c4', padding: 2 }}>
                        <FiX size={12} />
                      </button>
                    </span>
                  </div>
                  {s.texto && (
                    <p style={{ fontSize: 12, whiteSpace: 'pre-wrap', margin: '2px 0 0', color: '#4A6B8A' }}>{s.texto}</p>
                  )}
                </div>
              ))}
            </div>
          )}

          {clienteInterno && (formSecao ? (
            <div style={{ marginTop: 8, background: '#f7fafd', borderRadius: 8, padding: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
              <input
                value={formSecao.titulo}
                onChange={e => setFormSecao(f => ({ ...f, titulo: e.target.value }))}
                placeholder="Título da seção"
                autoFocus
                style={{ fontSize: 12, padding: '4px 6px', border: '1px solid #d8e3ee', borderRadius: 5, fontFamily: 'inherit' }}
              />
              <textarea
                value={formSecao.texto}
                onChange={e => setFormSecao(f => ({ ...f, texto: e.target.value }))}
                placeholder="Texto (opcional)"
                rows={2}
                style={{ fontSize: 12, padding: '4px 6px', border: '1px solid #d8e3ee', borderRadius: 5, fontFamily: 'inherit', resize: 'vertical' }}
              />
              <div style={{ display: 'flex', gap: 6 }}>
                <button type="button" onClick={confirmarFormSecao} disabled={!formSecao.titulo.trim()} style={{ fontSize: 11, background: '#0D1F3C', color: '#fff', border: 'none', borderRadius: 5, padding: '4px 10px', cursor: formSecao.titulo.trim() ? 'pointer' : 'default', opacity: formSecao.titulo.trim() ? 1 : 0.5, fontFamily: 'inherit' }}>
                  Salvar
                </button>
                <button type="button" onClick={cancelarFormSecao} style={{ fontSize: 11, background: 'none', color: '#6B8CAE', border: 'none', borderRadius: 5, padding: '4px 10px', cursor: 'pointer', fontFamily: 'inherit' }}>
                  Cancelar
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={abrirNovaSecao}
              style={{ marginTop: 8, fontSize: 11, fontWeight: 600, background: '#eef4fb', border: 'none', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', color: '#0D1F3C', fontFamily: 'inherit' }}
            >
              + Seção
            </button>
          ))}

          {clienteInterno && links.length > 0 && (
            <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {links.map(l => (
                <span key={l.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#eef4fb', borderRadius: 20, padding: '3px 4px 3px 10px', fontSize: 11 }}>
                  <a href={l.url} target="_blank" rel="noopener noreferrer" style={{ color: '#3f7fd6', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <FiLink2 size={10} /> {l.nome}
                  </a>
                  <button type="button" onClick={() => removerLink(l.id)} title="Remover link" style={{ display: 'flex', border: 'none', background: 'none', cursor: 'pointer', color: '#9ab0c4', padding: 2 }}>
                    <FiX size={11} />
                  </button>
                </span>
              ))}
            </div>
          )}

          {clienteInterno && (formLink ? (
            <div style={{ marginTop: 8, display: 'flex', gap: 6 }}>
              <input
                value={formLink.nome}
                onChange={e => setFormLink(f => ({ ...f, nome: e.target.value }))}
                placeholder="Nome"
                autoFocus
                style={{ flex: '0 0 120px', fontSize: 12, padding: '4px 6px', border: '1px solid #d8e3ee', borderRadius: 5, fontFamily: 'inherit' }}
              />
              <input
                value={formLink.url}
                onChange={e => setFormLink(f => ({ ...f, url: e.target.value }))}
                placeholder="https://..."
                style={{ flex: 1, fontSize: 12, padding: '4px 6px', border: '1px solid #d8e3ee', borderRadius: 5, fontFamily: 'inherit' }}
              />
              <button type="button" onClick={confirmarFormLink} disabled={!formLink.nome.trim() || !formLink.url.trim()} style={{ fontSize: 11, background: '#0D1F3C', color: '#fff', border: 'none', borderRadius: 5, padding: '4px 10px', cursor: formLink.nome.trim() && formLink.url.trim() ? 'pointer' : 'default', opacity: formLink.nome.trim() && formLink.url.trim() ? 1 : 0.5, fontFamily: 'inherit' }}>
                Adicionar
              </button>
              <button type="button" onClick={cancelarFormLink} style={{ fontSize: 11, background: 'none', color: '#6B8CAE', border: 'none', borderRadius: 5, padding: '4px 10px', cursor: 'pointer', fontFamily: 'inherit' }}>
                Cancelar
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={abrirNovoLink}
              style={{ marginTop: 8, fontSize: 11, fontWeight: 600, background: '#eef4fb', border: 'none', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', color: '#0D1F3C', fontFamily: 'inherit' }}
            >
              + Link
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── Auditoria: por enquanto só alimenta o modal "Metas internas" (dados da
   Nibo) e os modais de Particularidades/Fechamento -- a Grade de controle
   (colunas, vínculo de clientes) foi removida; "metasVisiveis"/
   "onToggleMetas" vêm de Rotinas (botão fica ao lado de "Sincronizar Nibo"
   no topo, só aparece nesta aba). ── */
function AuditoriaTab({ metasVisiveis, onToggleMetas, grupos, carregandoGrupos, grupoAtivoId }) {
  /* a grade principal daqui é a Execução dos Grupos de fluxo (cadastrados
     na aba Etapas) -- criar um grupo lá já faz ele aparecer aqui, sem
     precisar entrar no editor do grupo pra acompanhar o dia a dia. O resto
     desta função só existe pra alimentar o modal "Metas internas" (dados
     da Nibo) e os modais de Particularidades/Fechamento, cujos ícones
     ficaram dentro da visão "Lista" de Metas internas por enquanto.
     A lista de grupos e o grupo ativo vêm de Rotinas (o seletor mora na
     mesma linha das abas, lá em cima, pra economizar espaço em tela). */
  const [clientes, setClientes]         = useState([]); // nossa base -- resolve Particularidades/Fechamento por clienteNiboId
  const [idParticularidadesAberto, setIdParticularidadesAberto] = useState(null);
  const [idFechamentoAberto, setIdFechamentoAberto] = useState(null);
  const [mesRef, setMesRef]             = useState(() => new Date());

  const [dados, setDados]               = useState({ items: [] });
  const [carregandoDados, setCarregandoDados] = useState(true);
  const [erroDados, setErroDados]       = useState('');
  const [etapasPadrao, setEtapasPadrao] = useState([]);

  useEffect(() => {
    listarNossosClientes().then(setClientes).catch(() => {});
    listarEtapasObrigacao().then(r => setEtapasPadrao(r.items || [])).catch(() => {});
  }, []);

  /* obrigação Nibo -> etapa vinculada (só as ativas) -- usado pra manter
     visível ("Etapas pendentes") uma obrigação concluída na Nibo cuja etapa
     ainda não estiver 100% concluída pro cliente/mês. */
  const etapasPorObrigacao = useMemo(() => new Map(
    etapasPadrao.filter(e => e.ativo && e.obrigacao_id).map(e => [e.obrigacao_id, e])
  ), [etapasPadrao]);

  const grade   = construirGrade(mesRef);
  const inicio  = paraISO(grade[0]);
  const fim     = paraISO(grade[grade.length - 1]);

  const carregarDados = useCallback(() => {
    setCarregandoDados(true); setErroDados('');
    return buscarCalendarioComEtapas({ inicio, fim }, etapasPorObrigacao)
      .then(setDados)
      .catch(e => setErroDados(e.message))
      .finally(() => setCarregandoDados(false));
  }, [inicio, fim, etapasPorObrigacao]);

  useEffect(() => { carregarDados(); }, [carregarDados]);

  function trocarMes(delta) {
    setMesRef(m => new Date(m.getFullYear(), m.getMonth() + delta, 1));
  }

  function aoAtualizarCliente(atualizado) {
    setClientes(cs => cs.map(c => c.id === atualizado.id ? atualizado : c));
  }

  useEsc(onToggleMetas, !!metasVisiveis); // modal só fecha no Esc ou no X

  return (
    <div className={styles.calendarioLayout} style={{ display: 'block' }}>
      {carregandoGrupos ? (
        <p className={styles.vazio}>Carregando...</p>
      ) : grupos.length === 0 ? (
        <p className={styles.vazio}>Nenhum grupo de fluxo ativo ainda — crie um na aba Etapas pra ele aparecer aqui.</p>
      ) : (
        grupoAtivoId && <ExecucaoGrupoFluxo grupoId={grupoAtivoId} mostrarSeletorGrupo={false} />
      )}

      {metasVisiveis && (
        <div className={styles.metasOverlay}>
          <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 900 }} >
            <MetasInternasEspelho
              mesRef={mesRef}
              onTrocarMes={trocarMes}
              dados={dados}
              carregando={carregandoDados}
              erro={erroDados}
              onRecarregar={carregarDados}
              idsClientesFiltro={null}
              setorFiltro="todos"
              onFechar={onToggleMetas}
              clientes={clientes}
              onAbrirParticularidades={setIdParticularidadesAberto}
              onAbrirFechamento={setIdFechamentoAberto}
            />
          </div>
        </div>
      )}

      {idParticularidadesAberto && (
        <ModalParticularidades
          clienteInterno={clientes.find(c => c.id === idParticularidadesAberto) || null}
          nomeCliente={(() => {
            const c = clientes.find(x => x.id === idParticularidadesAberto);
            return c ? `${c.codigo} — ${c.nome}` : '';
          })()}
          onClienteAtualizado={aoAtualizarCliente}
          onClose={() => setIdParticularidadesAberto(null)}
        />
      )}

      {idFechamentoAberto && (() => {
        const c = clientes.find(x => x.id === idFechamentoAberto);
        return c ? (
          <ModalFechamento
            clienteNiboId={c.niboCustomerId}
            clienteInterno={c}
            nomeCliente={`${c.codigo} — ${c.nome}`}
            mesRef={mesRef}
            setorFiltro="todos"
            dados={dados}
            etapasPorObrigacao={etapasPorObrigacao}
            onClose={() => setIdFechamentoAberto(null)}
          />
        ) : null;
      })()}
    </div>
  );
}

/* ── Modal de vínculo de clientes a uma obrigação ──
   Lista quem já está vinculado (com opção de desvincular) e permite
   vincular um novo cliente — escolhido dentre os nossos clientes que já
   têm nibo_customer_id (evita duplicar cadastro, reaproveita nossa base). */
function ModalObrigacao({ obrigacao, onClose, syncTick }) {
  const [vinculados, setVinculados]   = useState([]);
  const [carregando, setCarregando]   = useState(true);
  const [erro, setErro]               = useState('');
  const [nossosClientes, setNossosClientes] = useState([]);
  const [usuarios, setUsuarios]       = useState([]);
  const [clienteEscolhido, setClienteEscolhido] = useState('');
  const [responsavelEscolhido, setResponsavelEscolhido] = useState('');
  const [vinculando, setVinculando]   = useState(false);
  const [desvinculando, setDesvinculando] = useState(null);
  const [atualizando, setAtualizando] = useState(false);
  useFecharComEsc(onClose);

  const carregarVinculados = useCallback(() => {
    setCarregando(true); setErro('');
    listarClientesDaObrigacao(obrigacao.id)
      .then(r => setVinculados(r.items || []))
      .catch(e => setErro(e.message))
      .finally(() => setCarregando(false));
  }, [obrigacao.id]);

  useEffect(() => { carregarVinculados(); }, [carregarVinculados, syncTick]);
  useEffect(() => {
    listarNossosClientes()
      .then(cs => setNossosClientes(cs.filter(c => c.niboCustomerId)))
      .catch(() => {});
    listarUsuariosNiboAPI({ apenasAtivos: true })
      .then(r => setUsuarios(r.items || []))
      .catch(() => {});
  }, []);

  const idsVinculados = new Set(vinculados.map(c => c.customerId));
  const disponiveis = nossosClientes.filter(c => !idsVinculados.has(c.niboCustomerId));

  async function handleVincular(e) {
    e.preventDefault();
    if (!clienteEscolhido || !responsavelEscolhido) return;
    setVinculando(true); setErro('');
    try {
      await vincularClientesObrigacaoNibo(obrigacao.id, {
        customerIds: [clienteEscolhido],
        inChargeUserId: responsavelEscolhido,
      });
      setClienteEscolhido('');
      carregarVinculados();
    } catch (e2) {
      setErro(e2.message);
    } finally {
      setVinculando(false);
    }
  }

  async function handleAtualizar() {
    setAtualizando(true); setErro('');
    try {
      await sincronizarNibo();
      carregarVinculados();
    } catch (e) {
      setErro('Não foi possível sincronizar com o Nibo agora: ' + e.message);
    } finally {
      setAtualizando(false);
    }
  }

  async function handleDesvincular(customerId) {
    if (!window.confirm('Desvincular este cliente desta obrigação?')) return;
    setDesvinculando(customerId); setErro('');
    try {
      await desvincularClienteObrigacaoNibo(obrigacao.id, customerId);
      carregarVinculados();
    } catch (e) {
      setErro(e.message);
    } finally {
      setDesvinculando(null);
    }
  }

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalBox} onClick={e => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <span className={styles.modalTitulo}>{obrigacao.description || obrigacao.nickname}</span>
          <button className={styles.modalBtnX} onClick={onClose}><FiX size={16} /></button>
        </div>
        <div className={styles.modalBody}>
          {erro && <p className={styles.vazio}>{erro}</p>}

          <p className={styles.modalSecaoTitulo}><FiLink2 size={12} /> Vincular cliente</p>
          <form onSubmit={handleVincular} className={styles.vincularForm} style={{ flexDirection: 'column', alignItems: 'stretch' }}>
            <select
              className={styles.filtroPeriodoInput}
              value={clienteEscolhido}
              onChange={e => setClienteEscolhido(e.target.value)}
            >
              <option value="">
                {nossosClientes.length === 0 ? 'Nenhum cliente seu vinculado ao Nibo ainda' : 'Selecione um cliente...'}
              </option>
              {disponiveis.map(c => <option key={c.id} value={c.niboCustomerId}>{c.nome}</option>)}
            </select>
            <select
              className={styles.filtroPeriodoInput}
              value={responsavelEscolhido}
              onChange={e => setResponsavelEscolhido(e.target.value)}
            >
              <option value="">
                {usuarios.length === 0 ? 'Carregando responsáveis...' : 'Responsável...'}
              </option>
              {usuarios.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
            <button
              type="submit"
              className={styles.btnBuscar}
              disabled={!clienteEscolhido || !responsavelEscolhido || vinculando}
              style={{ alignSelf: 'flex-start' }}
            >
              <FiPlus size={13} /> {vinculando ? 'Vinculando...' : 'Vincular'}
            </button>
          </form>
          {nossosClientes.length > 0 && disponiveis.length === 0 && (
            <p className={styles.muted}>Todos os seus clientes vinculados ao Nibo já estão nesta obrigação.</p>
          )}

          <div className={styles.modalSecaoTituloComAcao} style={{ marginTop: 16 }}>
            <p className={styles.modalSecaoTitulo}>Clientes vinculados</p>
            <button type="button" className={styles.btnAtualizarSecao} onClick={handleAtualizar} disabled={atualizando || carregando}>
              <FiRefreshCw size={12} className={atualizando ? styles.girando : ''} /> {atualizando ? 'Sincronizando...' : 'Sincronizar'}
            </button>
          </div>
          {carregando ? (
            <p className={styles.vazio}>Carregando...</p>
          ) : vinculados.length === 0 ? (
            <p className={styles.vazio}>Nenhum cliente vinculado.</p>
          ) : (
            <ul className={styles.listaSimples}>
              {vinculados.map(c => (
                <li key={c.customerId} className={styles.linhaVinculado}>
                  <span>
                    <strong>{c.customerName}</strong>
                    {c.customerDocumentNumber && <span> — {c.customerDocumentNumber}</span>}
                  </span>
                  <button
                    type="button"
                    className={styles.atencaoRemover}
                    onClick={() => handleDesvincular(c.customerId)}
                    disabled={desvinculando === c.customerId}
                    title="Desvincular"
                  >
                    <FiX size={13} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Modal de detalhe de um protocolo — histórico, documentos e monitoramento
   de e-mail (quem recebeu, enviado/aberto/visualizado), espelhando a tela
   "Detalhes do documento" do Nibo ── */
function ModalProtocolo({ item, onClose }) {
  const [detalhe, setDetalhe]     = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro]           = useState('');
  const [arquivoAberto, setArquivoAberto] = useState(null); // { url, fileName }
  useFecharComEsc(onClose);

  useEffect(() => {
    setCarregando(true); setErro('');
    buscarDetalheProtocoloNibo(item.customer?.id, item.id)
      .then(setDetalhe)
      .catch(e => setErro(e.message))
      .finally(() => setCarregando(false));
  }, [item]);

  const h  = detalhe?.historico;
  const st = STATUS_PROTOCOLO[item.status] || { label: item.status ?? '—', css: 'statusNeutro' };
  const hojeStr   = paraISO(new Date());
  const vencido    = item.dueDate && item.dueDate < hojeStr && ![4, 8].includes(item.status);

  return (
    <div className={styles.painelOverlay}>
      {arquivoAberto && (
        <div className={styles.visualizadorArquivo} onClick={e => e.stopPropagation()}>
          <div className={styles.visualizadorHeader}>
            <span className={styles.visualizadorNome}>{arquivoAberto.fileName}</span>
            <button className={styles.painelBtnXEscuro} onClick={() => setArquivoAberto(null)}><FiX size={16} /></button>
          </div>
          <iframe src={arquivoAberto.url} title={arquivoAberto.fileName} className={styles.visualizadorFrame} />
        </div>
      )}
      <div className={styles.painelLateral} onClick={e => e.stopPropagation()}>
        <div className={styles.painelHeader}>
          <div>
            <span className={styles.painelTitulo}>{item.obligation?.name || '—'}</span>
            <p className={styles.painelSubtitulo}>{item.department?.name || '—'} · {item.customer?.name || '—'}</p>
          </div>
          <button className={styles.painelBtnX} onClick={onClose}><FiX size={18} /></button>
        </div>

        <div className={styles.painelBody}>
          {carregando && <p className={styles.vazio}>Carregando...</p>}
          {erro && <p className={styles.vazio}>{erro}</p>}

          {!carregando && !erro && (
            <>
              <div className={styles.protocoloStats}>
                <div className={styles.protocoloStatItem}>
                  <span className={styles.statLabelMini}>Competência</span>
                  <span className={styles.statValorMini}>{h?.accrual || item.accrual || '—'}</span>
                </div>
                <div className={styles.protocoloStatItem}>
                  <span className={styles.statLabelMini}>Vencimento</span>
                  <span className={`${styles.statValorMini} ${vencido ? styles.statValorAlerta : ''}`}>{formatarData(item.dueDate)}</span>
                </div>
                <div className={styles.protocoloStatItem}>
                  <span className={styles.statLabelMini}>Valor</span>
                  <span className={`${styles.statValorMini} ${styles.statValorDinheiro}`}>{formatarMoeda(item.value)}</span>
                </div>
                <div className={styles.protocoloStatItem}>
                  <span className={styles.statLabelMini}>Status</span>
                  <span className={`${styles.statusBadge} ${styles[st.css]}`}>{st.label}</span>
                </div>
              </div>

              <div className={styles.painelSecao}>
                <p className={styles.modalSecaoTitulo}><FiUser size={12} /> Cliente</p>
                <p className={styles.textoCorpo}>
                  {item.customer?.name || '—'}
                  {item.customer?.documentNumber && <span className={styles.muted}> · {item.customer.documentNumber}</span>}
                </p>
              </div>

              <div className={styles.painelSecao}>
                <p className={styles.modalSecaoTitulo}><FiFileText size={12} /> Protocolo</p>
                <p className={styles.textoCorpo}>
                  Nº {h?.number || item.number} · protocolado em {formatarDataHora(h?.filedDate || item.filedDate)}
                  {h?.filedInChargeName && <span> por {h.filedInChargeName}</span>}
                </p>
                <p className={styles.muted}>
                  Recálculo {h?.recalculationEnabled ? 'habilitado' : 'desabilitado'}
                </p>
              </div>

              <div className={styles.painelSecao}>
                <p className={styles.modalSecaoTitulo}><FiPaperclip size={12} /> Documentos</p>
                {(detalhe.arquivos || []).length === 0 ? (
                  <p className={styles.vazio}>Nenhum documento.</p>
                ) : (
                  <ul className={styles.listaSimples}>
                    {detalhe.arquivos.map(a => (
                      <li key={a.fileId}>
                        <button
                          type="button"
                          className={styles.linkVisualizar}
                          onClick={() => setArquivoAberto({ url: a.url, fileName: a.fileName })}
                        >
                          {a.fileName}
                        </button>
                        {a.downloadUrl && (
                          <>
                            {' · '}
                            <a href={a.downloadUrl} target="_blank" rel="noopener noreferrer" className={styles.muted}>baixar</a>
                          </>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className={styles.painelSecao}>
                <p className={styles.modalSecaoTitulo}><FiSend size={12} /> Monitoramento</p>
                <div className={styles.monitoramentoFlags}>
                  <span className={`${styles.flagMonitoramento} ${detalhe.emails?.hasEmailSent ? styles.flagAtiva : ''}`}>
                    <FiSend size={11} /> Enviado
                  </span>
                  <span className={`${styles.flagMonitoramento} ${detalhe.emails?.hasEmailOpened ? styles.flagAberta : ''}`}>
                    <FiMail size={11} /> Aberto
                  </span>
                  <span className={`${styles.flagMonitoramento} ${detalhe.emails?.hasDocumentVisualized ? styles.flagVisualizada : ''}`}>
                    <FiEye size={11} /> Visualizado
                  </span>
                </div>

                {(detalhe.emails?.emails || []).length === 0 ? (
                  <p className={styles.vazio}>Nenhum destinatário.</p>
                ) : (
                  detalhe.emails.emails.map((e, i) => (
                    <div key={i} className={styles.monitoramentoItem}>
                      <div className={styles.monitoramentoNome}>
                        {e.name} <span className={styles.muted}>{e.email}</span>
                      </div>
                      <ul className={styles.eventosLista}>
                        {[...e.events].reverse().map((ev, j) => (
                          <li key={j}>{formatarDataHora(ev.date)} — {ev.eventDescription}</li>
                        ))}
                      </ul>
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Tarefas (aba ao lado de Auditoria): mesma listagem agrupada por dia
   do card "Metas internas" (por atividade/cliente/lista, badge de
   contagem, ratio de concluídas), só que com as tarefas internas, sem os
   prazos da Nibo -- é o padrão pedido ("igual o modal das Metas internas"),
   não a página de cadastro. Criar/editar/excluir tarefa continua sendo a
   página "Tarefas" do menu lateral (Tarefas.jsx); aqui é só acompanhamento. */
function TarefasTab() {
  const [mesRef, setMesRef]       = useState(() => new Date());
  const [dados, setDados]         = useState({ items: [] });
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro]           = useState('');

  const grade  = construirGrade(mesRef);
  const inicio = paraISO(grade[0]);
  const fim    = paraISO(grade[grade.length - 1]);

  const carregar = useCallback(() => {
    setCarregando(true); setErro('');
    return buscarTarefasComoMetas({ inicio, fim })
      .then(setDados)
      .catch(e => setErro(e.message))
      .finally(() => setCarregando(false));
  }, [inicio, fim]);
  useEffect(() => { carregar(); }, [carregar]);

  function trocarMes(delta) {
    setMesRef(m => new Date(m.getFullYear(), m.getMonth() + delta, 1));
  }

  return (
    <MetasInternasEspelho
      titulo="Tarefas"
      mesRef={mesRef}
      onTrocarMes={trocarMes}
      dados={dados}
      carregando={carregando}
      erro={erro}
      onRecarregar={carregar}
      idsClientesFiltro={null}
      setorFiltro="todos"
      clientes={[]}
      onAbrirParticularidades={() => {}}
      onAbrirFechamento={() => {}}
    />
  );
}

/* ── Etapas: grupos de fluxo (linha do tempo + execução mensal) --
   substitui o antigo cadastro de "molde de checklist vinculado a
   obrigação" (etapas_obrigacao_padrao). O backend antigo (rotas
   /etapas-obrigacao, usado pelo ModalFechamento da Auditoria) continua
   ativo por enquanto -- só a tela mudou. Ver frontend/src/modulos/rotinas/
   GruposFluxo.jsx e backend/sql/grupos_fluxo.sql. ── */
function EtapasTab() {
  return <GruposFluxo />;
}

/* ── Página principal ── */
/* dias úteis (seg-sex) entre uma data e agora -- não desconta feriado,
   só fins de semana; suficiente pro alerta de "parado há mais de 5 dias
   úteis" deste bloco (somente leitura, não edita nada aqui) */
function diasUteisDesde(iso) {
  if (!iso) return 0;
  const inicio = new Date(iso);
  const fim = new Date();
  let dias = 0;
  const cursor = new Date(inicio);
  cursor.setHours(0, 0, 0, 0);
  const fimDia = new Date(fim);
  fimDia.setHours(0, 0, 0, 0);
  while (cursor < fimDia) {
    cursor.setDate(cursor.getDate() + 1);
    const diaSemana = cursor.getDay();
    if (diaSemana !== 0 && diaSemana !== 6) dias += 1;
  }
  return dias;
}

/* bloco somente leitura -- não edita processo nenhum, só resume e linka
   pro módulo Processos (Obrigações > Processos) */
function ProcessosResumoBloco() {
  const navigate = useNavigate();
  const [processos, setProcessos] = useState([]);

  useEffect(() => {
    listarProcessos({ status: 'em_andamento' }).then(r => setProcessos(r.items || [])).catch(() => {});
  }, []);

  const total   = processos.length;
  const parados = processos.filter(p => diasUteisDesde(p.data_entrada_etapa) > 5).length;
  const [travados, setTravados] = useState(0);

  useEffect(() => {
    listarProcessos({ status: 'travado' }).then(r => setTravados((r.items || []).length)).catch(() => {});
  }, []);

  return (
    <div className={styles.processosResumo}>
      <span className={styles.processosResumoTitulo}>Processos em andamento</span>
      <div className={styles.processosResumoStats}>
        <div className={styles.processosResumoItem}>
          <span className={styles.processosResumoNum}>{total}</span>
          <span className={styles.processosResumoLabel}>Em andamento</span>
        </div>
        <div className={styles.processosResumoItem}>
          <span className={`${styles.processosResumoNum} ${parados > 0 ? styles.processosResumoAlerta : ''}`}>{parados}</span>
          <span className={styles.processosResumoLabel}>Parados há mais de 5 dias úteis</span>
        </div>
        <div className={styles.processosResumoItem}>
          <span className={`${styles.processosResumoNum} ${travados > 0 ? styles.processosResumoAlerta : ''}`}>{travados}</span>
          <span className={styles.processosResumoLabel}>Travados</span>
        </div>
      </div>
      <button type="button" className={styles.processosResumoLink} onClick={() => navigate('/app/rotinas/processos')}>
        Ver todos <FiExternalLink size={12} />
      </button>
    </div>
  );
}

function Rotinas() {
  const location = useLocation();
  /* a aba Rotinas (auditoria) está oculta -- o Painel abre em Vencimentos */
  const [abaAtiva, setAbaAtiva] = useState('vencimentos');

  /* "sem tarefas"/"tarefas x/y" na grade de execução navega pra cá com
     ?aba=tarefas -- essa página é uma aba persistente (fica montada, só
     escondida, ao trocar de rota dentro do app), então um useState
     inicial não bastaria pra reagir a uma segunda visita; precisa reagir
     à mudança da query string mesmo com o componente já montado. */
  useEffect(() => {
    const aba = new URLSearchParams(location.search).get('aba');
    if (aba === 'tarefas') setAbaAtiva('tarefas');
    /* vindo dos endereços antigos do menu (Tarefas, Rotinas, Processos) */
    if (['tarefas_lista', 'cadastro', 'processos', 'vencimentos'].includes(aba)) setAbaAtiva(aba);
  }, [location.search]);
  const [protocoloSelecionado, setProtocoloSelecionado] = useState(null);
  const [obrigacaoSelecionada, setObrigacaoSelecionada] = useState(null);
  const [sincronizando, setSincronizando] = useState(false);
  const [erroSincronizar, setErroSincronizar] = useState('');
  const [syncTick, setSyncTick] = useState(0);
  const [metasVisiveis, setMetasVisiveis] = useState(false); // "Metas internas" (Auditoria) -- modal, oculto por padrão

  /* grupos de fluxo (aba Rotinas) -- mora aqui, não dentro de AuditoriaTab,
     porque o seletor de grupo vive na mesma linha das abas, ao lado de
     "Sincronizar Nibo", pra economizar espaço em tela. */
  const [grupos, setGrupos] = useState([]);
  const [grupoAtivoId, setGrupoAtivoId] = useState(null);
  const [carregandoGrupos, setCarregandoGrupos] = useState(true);
  useEffect(() => {
    listarGrupos()
      .then(r => {
        const ativos = (r.items || []).filter(g => g.ativo);
        setGrupos(ativos);
        setGrupoAtivoId(prev => prev ?? ativos[0]?.id ?? null);
      })
      .catch(() => {})
      .finally(() => setCarregandoGrupos(false));
  }, []);

  const protocolos  = usePaginado(listarProtocolosNibo, { dataInicio: primeiroDiaMes(), dataFim: ultimoDiaMes() });
  const obrigacoes  = usePaginado(listarObrigacoesNibo);

  const abas = { protocolos, obrigacoes };

  /* refs sempre atualizados, sem precisar recriar o listener a cada render */
  const abaAtivaRef = useRef(abaAtiva);
  abaAtivaRef.current = abaAtiva;
  const abasRef = useRef(abas);
  abasRef.current = abas;

  function trocarAba(key) {
    setAbaAtiva(key);
    abas[key]?.garantirCarregado();
  }

  /* zera o cache de 5 min do backend e força buscar tudo de novo na Nibo —
     calendário, obrigações e protocolos, independente da aba ativa */
  async function handleSincronizar() {
    setSincronizando(true); setErroSincronizar('');
    try {
      await sincronizarNibo();
      setSyncTick(t => t + 1);
      protocolos.carregar(protocolos.pagina);
      obrigacoes.carregar(obrigacoes.pagina);
    } catch (e) {
      setErroSincronizar(e.message);
    } finally {
      setSincronizando(false);
    }
  }

  const mockAtivo = abas[abaAtiva]?.dados?.mock;

  /* atualiza a aba de tabela ativa sozinha quando o usuário volta o foco
     pra janela — ex.: depois de disparar os documentos no Nibo em outra aba */
  useEffect(() => {
    function aoVoltarFoco() {
      const key = abaAtivaRef.current;
      if (key === 'calendario') return; // o Calendário já se atualiza sozinho
      const aba = abasRef.current[key];
      aba?.carregar(aba.pagina);
    }
    window.addEventListener('focus', aoVoltarFoco);
    return () => window.removeEventListener('focus', aoVoltarFoco);
  }, []);

  return (
    <div className={styles.page}>
      <div className={styles.tabsRow}>
        {abaAtiva === 'auditoria' && grupos.length > 1 && (
          <select
            className={styles.filtroGrupoTopo}
            style={{ marginBottom: 8 }}
            value={grupoAtivoId || ''}
            onChange={e => setGrupoAtivoId(e.target.value)}
          >
            {grupos.map(g => (
              <option key={g.id} value={g.id}>{g.nome}</option>
            ))}
          </select>
        )}
        <div className={styles.tabs}>
          {[
            ['auditoria', 'Rotinas'], // aba oculta (o Painel abre em Vencimentos) -- não remover, só não renderizar o botão
            ['vencimentos', 'Vencimentos'], // modelo 2: mesmo dado da aba Rotinas, colunas por data
            /* páginas que eram itens do menu Obrigações (31/10/2026), iguais a antes */
            ['tarefas_lista', 'Tarefas'],
            ['cadastro', 'Rotinas'],
            ['tarefas', 'Tarefas'], // aba oculta (tarefas aparecem em Vencimentos) -- não remover, só não renderizar o botão
            ['processos', 'Processos'],
            ['calendario', 'Calendário'], // aba oculta (duplicava a Auditoria) -- não remover, só não renderizar o botão
            ['protocolos', 'Protocolos'],
            ['obrigacoes', 'Obrigações'],
            ['etapas', 'Etapas'],
          /* ABAS OCULTAS (31/10/2026, pedido do usuário): Rotinas, Calendário,
             Tarefas, Protocolos, Obrigações e Etapas, e o botão Sincronizar
             Nibo -- as obrigações não vão mais ser vinculadas à Nibo. O código
             fica; depois que Vencimentos + Processos estiverem funcionando
             100%, essas páginas ocultas serão APAGADAS do código. */
          ].filter(([key]) => !ABAS_OCULTAS.includes(key) && podeVerAba(key)).map(([key, label]) => (
            <button key={key}
              className={`${styles.tabBtn} ${abaAtiva === key ? styles.tabBtnAtiva : ''}`}
              onClick={() => trocarAba(key)}>
              {label}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
          {abaAtiva === 'auditoria' && (
            <button
              type="button"
              className={styles.btnSincronizarTudo}
              style={{ marginBottom: 0 }}
              onClick={() => setMetasVisiveis(v => !v)}
            >
              <FiEye size={13} /> {metasVisiveis ? 'Ocultar Metas internas' : 'Ver Metas internas'}
            </button>
          )}
          {!OCULTAR_SINCRONIZAR_NIBO && <button
            type="button"
            className={styles.btnSincronizarTudo}
            style={{ marginBottom: 0 }}
            onClick={handleSincronizar}
            disabled={sincronizando}
          >
            <FiRefreshCw size={13} className={sincronizando ? styles.girando : ''} />
            {sincronizando ? 'Sincronizando...' : 'Sincronizar Nibo'}
          </button>}
        </div>
      </div>

      {erroSincronizar && (
        <div className={styles.avisoFaixa}>
          <FiAlertTriangle size={14} />
          Não foi possível sincronizar com o Nibo agora: {erroSincronizar}
          <button type="button" className={styles.avisoFechar} onClick={() => setErroSincronizar('')}><FiX size={14} /></button>
        </div>
      )}

      {abaAtiva === 'calendario' ? (
        <CalendarioTab syncTick={syncTick} />
      ) : abaAtiva === 'processos' ? (
        <>
          <ProcessosResumoBloco />
          <Processos />
        </>
      ) : abaAtiva === 'auditoria' ? (
        <AuditoriaTab
          metasVisiveis={metasVisiveis}
          onToggleMetas={() => setMetasVisiveis(v => !v)}
          grupos={grupos}
          carregandoGrupos={carregandoGrupos}
          grupoAtivoId={grupoAtivoId}
        />
      ) : abaAtiva === 'vencimentos' ? (
        <VisaoVencimentos />
      ) : abaAtiva === 'tarefas_lista' ? (
        <TarefasPagina />
      ) : abaAtiva === 'cadastro' ? (
        <CadastroRotinas />
      ) : abaAtiva === 'tarefas' ? (
        <TarefasTab />
      ) : abaAtiva === 'etapas' ? (
        <EtapasTab />
      ) : (
        <>
          {mockAtivo && <AvisoMock />}
          <BarraLista
            aba={abas[abaAtiva]}
            filtroExtra={abaAtiva === 'protocolos' && (
              <FiltroPeriodo filtros={protocolos.filtros} aplicarFiltros={protocolos.aplicarFiltros} />
            )}
            acoesExtra={abaAtiva === 'obrigacoes' && (
              <label className={styles.toggleWrap}>
                <input
                  type="checkbox"
                  checked={!!obrigacoes.filtros.incluirInativas}
                  onChange={e => obrigacoes.aplicarFiltros({ incluirInativas: e.target.checked || undefined })}
                />
                <span className={styles.toggleTrilho}><span className={styles.toggleBola} /></span>
                Exibir itens inativos
              </label>
            )}
          />

          <div className={styles.tabelaWrap}>
            {abaAtiva === 'protocolos' && (
              <table className={styles.tabela}>
                <thead><tr><th>Obrigação</th><th>Cliente</th><th>Referência</th><th>Vencimento</th><th>Valor</th><th>Status</th></tr></thead>
                <tbody>
                  <EstadoTabela aba={protocolos} colSpan={6} />
                  {!protocolos.carregando && !protocolos.erro && protocolos.dados.items.map(p => {
                    const st = STATUS_PROTOCOLO[p.status] || { label: p.status ?? '—', css: 'statusNeutro' };
                    return (
                      <tr key={p.id} className={styles.linhaClicavel} onClick={() => setProtocoloSelecionado(p)}>
                        <td>{p.obligation?.name || '—'}</td>
                        <td>{p.customer?.name || '—'}</td>
                        <td>{p.accrual || '—'}</td>
                        <td>{formatarData(p.dueDate)}</td>
                        <td>{formatarMoeda(p.value)}</td>
                        <td><span className={`${styles.statusBadge} ${styles[st.css]}`}>{st.label}</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}

            {abaAtiva === 'obrigacoes' && (
              <table className={styles.tabela}>
                <thead><tr><th>Obrigação</th><th>Tipo</th><th>Departamento</th><th>Apelido</th><th>Frequência</th><th>Robô padrão</th><th>Status</th></tr></thead>
                <tbody>
                  <EstadoTabela aba={obrigacoes} colSpan={7} />
                  {!obrigacoes.carregando && !obrigacoes.erro && obrigacoes.dados.items.map(o => (
                    <tr key={o.id} className={styles.linhaClicavel} onClick={() => setObrigacaoSelecionada(o)}>
                      <td>{o.description || o.nickname || '—'}</td>
                      <td>{TIPO_OBRIGACAO[o.type] || '—'}</td>
                      <td>{o.department?.name || '—'}</td>
                      <td>{o.nickname || '—'}</td>
                      <td>{FREQUENCIA_OBRIGACAO[o.frequency] ?? '—'}</td>
                      <td>{o.automaticRead ? 'Sim' : 'Não'}</td>
                      <td>
                        <span className={`${styles.statusBadge} ${o.isActive ? styles.statusOk : styles.statusNeutro}`}>
                          {o.isActive ? 'Ativa' : 'Inativa'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <Paginacao aba={abas[abaAtiva]} />
          </div>
        </>
      )}

      {protocoloSelecionado && (
        <ModalProtocolo item={protocoloSelecionado} onClose={() => setProtocoloSelecionado(null)} />
      )}
      {obrigacaoSelecionada && (
        <ModalObrigacao obrigacao={obrigacaoSelecionada} onClose={() => setObrigacaoSelecionada(null)} syncTick={syncTick} />
      )}
    </div>
  );
}

export default Rotinas;
